import { useState, useEffect, useMemo, useCallback } from 'react';
import { Battery, Fuel, Building2, Download, Plus, Minus, Trash2, Calculator, X } from 'lucide-react';
import { usePeriod } from '../contexts/PeriodContext';
import { useUseCase } from '../contexts/UseCaseContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { useProfitabilitySnapshot } from '../contexts/ProfitabilitySnapshotContext';
import { standardiseVehicleId } from '../utils/vehicleIdStandardise';
import { logger } from '../utils/logger';
import PeriodSelector from '../components/PeriodSelector';
import { createPortal } from 'react-dom';
import LineChart, { LineChartSeries } from '../components/LineChart';
import { redactVehicleType, redactVehicleId } from '../utils/demoRedaction';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';
import BarChart from '../components/BarChart'
import { Button } from '@/components/ui/button';

interface VehicleType {
  id: string;
  name: string;
  quantity: number;
  unitCost: number;
  serviceLife: number; // years
  operationalCost: number; // per year
  fuelCost: number; // per year (diesel)
  evCost?: number; // EV unit cost
  evOperationalCost?: number; // EV operational cost per year
  evChargingCost?: number; // EV charging cost per year (£/kWh)
  chargerCost?: number; // Charger installation cost
  futureDieselQuantity?: number; // Future diesel quantity after replacements
  futureEVQuantity?: number; // Future EV quantity after replacements
  /** Per-fuel quantity breakdown from vehicle list CSV (e.g. { "Diesel": 31, "Electric": 2 }) */
  fuelTypeBreakdown?: Record<string, number>;
  // Capital cost fields
  residualValue?: number; // % of purchase price
  conversionCost?: number; // EV conversion/modification cost £
  initialTaxesFees?: number; // Registration, import duties £
  // Operational cost fields
  insurance?: number; // £/yr
  roadTax?: number; // £/yr
  oLicense?: number; // £/yr
  telematics?: number; // £/mo
  cleaning?: number; // £/mo
  parking?: number; // £/mo
  permits?: number; // £/yr
  driverCost?: number; // £/yr
  overhead?: number; // % of operational costs
}

interface ReplacementProject {
  id: string;
  vehicleTypeId: string;
  year: number;
  quantity: number;
  isEV: boolean;
  isDiesel: boolean;
  stationId?: string;
  planId?: string; // Associate project with a plan
}

/** Capital-cost fields editable in the reference table (all numeric). */
type CostManageNumericField = 'residualValue' | 'conversionCost' | 'initialTaxesFees';

/** Operational-cost fields editable in the reference table. */
type OperationalCostField =
  | 'insurance'
  | 'roadTax'
  | 'oLicense'
  | 'telematics'
  | 'cleaning'
  | 'parking'
  | 'permits'
  | 'overhead';

interface Plan {
  id: string;
  name: string;
  color: string;
  isActive: boolean; // Whether this plan is currently being edited/viewed
}

interface InfrastructureProject {
  id: string;
  name: string;
  year: number;
  stationCount: number;
  chargerCount: number;
  unitCost: number;
  totalCost: number;
}

/** Canonical ES vehicle type ids (aligned with fleet list MAPPED VEHICLE TYPE / Fleet Report). */
const ES_COST_MANAGE_TYPE_ORDER = ['response-car', 'small-van', 'large-van', 'pumping-appliance', 'other-appliance'] as const;

/**
 * Map free-text vehicle type (fleet list, vehicle-type CSV, Supatrak AssetType) to Cost Manage ES type id.
 * Unknown labels roll up to other-appliance so counts are not lost.
 */
function mapRawLabelToEsCostManageTypeId(raw: string): string {
  const s = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return 'other-appliance';
  if (s === 'response car' || (s.includes('response') && s.includes('car'))) return 'response-car';
  if (s === 'small van' || (/\bsmall\b/.test(s) && /\bvan\b/.test(s))) return 'small-van';
  if (s === 'large van' || (/\blarge\b/.test(s) && /\bvan\b/.test(s))) return 'large-van';
  if (s === 'pumping appliance' || s.includes('pumping') || /\bpump(ing)?\s+appliance\b/.test(s)) return 'pumping-appliance';
  if (s === 'other appliance' || s.includes('other appliance')) return 'other-appliance';
  if (/\bappliance\b/.test(s)) return 'other-appliance';
  return 'other-appliance';
}

const START_YEAR = 2026;
const END_YEAR = START_YEAR + 10;

export default function CostManage() {
  const { selectedPeriod } = usePeriod();
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  
  // ✅ Parse CSV line - handle quoted values and commas within quotes
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let charIdx = 0; charIdx < line.length; charIdx++) {
      const char = line[charIdx];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    // Add the last value
    values.push(currentValue.trim());
    return values;
  };
  
  // Default vehicle types based on mode (and anonymised for demo users)
  const defaultVehicleTypes: VehicleType[] = useMemo(() => {
    if (useCase === 'emergency') {
      // Emergency Services mode: Response car, Small van, Large van, Pumping appliance, Other appliance
      return [
        // Use rounded, synthetic fleet sizes/costs when in demo mode so numbers don't reflect any real organisation.
        { id: 'response-car', name: 'Response Car', quantity: isDemoMode ? 60 : 75, unitCost: 45000, serviceLife: 5, operationalCost: 2000, fuelCost: isDemoMode ? 2800 : 3000, evCost: 45000, evOperationalCost: 1500, evChargingCost: 0.15, chargerCost: 5000 },
        { id: 'small-van', name: 'Small Van', quantity: isDemoMode ? 40 : 46, unitCost: 65000, serviceLife: 5, operationalCost: 2500, fuelCost: isDemoMode ? 3600 : 4000, evCost: 70000, evOperationalCost: 2000, evChargingCost: 0.15, chargerCost: 8000 },
        { id: 'large-van', name: 'Large Van', quantity: isDemoMode ? 14 : 11, unitCost: 80000, serviceLife: 7, operationalCost: 3000, fuelCost: isDemoMode ? 5200 : 5000, evCost: 90000, evOperationalCost: 2500, evChargingCost: 0.15, chargerCost: 10000 },
        { id: 'pumping-appliance', name: 'Pumping Appliance', quantity: isDemoMode ? 36 : 43, unitCost: 100000, serviceLife: 12, operationalCost: 5000, fuelCost: isDemoMode ? 6800 : 6000, evCost: 350000, evOperationalCost: 4000, evChargingCost: 0.15, chargerCost: 50000 },
        { id: 'other-appliance', name: 'Other Appliance', quantity: isDemoMode ? 16 : 19, unitCost: 350000, serviceLife: 12, operationalCost: 6000, fuelCost: isDemoMode ? 7400 : 7000, evCost: 350000, evOperationalCost: 5000, evChargingCost: 0.15, chargerCost: 50000 },
      ];
    } else {
      // Logistics mode: Only Diesel Truck and Electric Truck
      return [
        { id: 'diesel-truck', name: 'Diesel Truck', quantity: isDemoMode ? 42 : 50, unitCost: 80000, serviceLife: 10, operationalCost: 4000, fuelCost: isDemoMode ? 7500 : 8000, evCost: 120000, evOperationalCost: 3000, evChargingCost: 0.15, chargerCost: 15000 },
        { id: 'electric-truck', name: 'Electric Truck', quantity: isDemoMode ? 8 : 0, unitCost: 120000, serviceLife: 10, operationalCost: 3000, fuelCost: 0, evCost: 120000, evOperationalCost: 3000, evChargingCost: 0.15, chargerCost: 15000 },
      ];
    }
  }, [useCase, isDemoMode]);

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>(defaultVehicleTypes);
  const [replacementProjects, setReplacementProjects] = useState<ReplacementProject[]>([]);
  const [infrastructureProjects] = useState<InfrastructureProject[]>([]);
  
  // Plan color palette (categorical — shared Chart palette)
  const planColors = CHART_PALETTE;
  
  // Initialize with Plan 1 by default
  const defaultPlan: Plan = {
    id: 'plan-default-1',
    name: 'Plan 1',
    color: planColors[0],
    isActive: true,
  };
  const [plans, setPlans] = useState<Plan[]>([defaultPlan]);
  const [activePlanId, setActivePlanId] = useState<string | null>('plan-default-1');
  const [selectedYear, setSelectedYear] = useState<number>(START_YEAR);
  const [editingCapitalCostField, setEditingCapitalCostField] = useState<{ vehicleId: string; field: string } | null>(null);
  const [editingOperationalCostField, setEditingOperationalCostField] = useState<{ vehicleId: string; field: string } | null>(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProject, setNewProject] = useState<Partial<ReplacementProject>>({ year: START_YEAR, quantity: 1, isEV: false, isDiesel: false });
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editProjectData, setEditProjectData] = useState<{ isDiesel: boolean; isEV: boolean; quantity: number; isRemoval: boolean } | null>(null);
  const [annualIncreasePercent, setAnnualIncreasePercent] = useState<number>(3);
  const [editingAnnualIncrease, setEditingAnnualIncrease] = useState(false);
  const [, setVehicleTypeCSV] = useState<Array<Record<string, string>>>([]);
  const [, setFleetListCSV] = useState<Array<Record<string, string>>>([]);
  const [activeFleetView, setActiveFleetView] = useState<'current' | 'future'>('current');
  const [activeFleetSource] = useState<'supatrak' | 'optifleet'>('supatrak');
  const [supatrakVehicleTypes, setSupatrakVehicleTypes] = useState<VehicleType[]>([]);
  const [supatrakListLoading, setSupatrakListLoading] = useState(false);
  const [supatrakListError, setSupatrakListError] = useState<string | null>(null);
  const [showEditableTooltip] = useState(false);
  const [editableTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [editableTooltipText] = useState<{ title: string; description: string } | null>(null);
  const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null);
  const [dragOverYear, setDragOverYear] = useState<number | null>(null);
  const [dragOverVehicleTypeId, setDragOverVehicleTypeId] = useState<string | null>(null);
  const [showUnmappedVehiclesModal, setShowUnmappedVehiclesModal] = useState(false);
  /** Map vehicle ID (e.g. reg) to vehicle type id for aggregating profitability by type (logistics) */
  const [vehicleIdToTypeId, setVehicleIdToTypeId] = useState<Record<string, string>>({});
  /** TCO tooltip position + target (vehicle id or 'total') for portal positioning */
  const [tcoTooltip, setTcoTooltip] = useState<{ left: number; top: number; vehicleId: string | null; isTotal?: boolean } | null>(null);

  // Fetch fleet list to get real vehicle quantities (skipped for demo users so numbers stay synthetic)
  useEffect(() => {
    if (isDemoMode) {
      // In demo mode we rely on the synthetic defaults above and avoid pulling any real fleet CSVs.
      return;
    }
    let isCancelled = false;
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      // For Emergency Services: use fleet list CSV (lightfleet and appliances CSVs, NOT telematics)
      // For Logistics: use vehicle level CSV (if period is selected) to get unique vehicle counts
      if (useCase === 'emergency') {
        authenticatedFetch(API_ENDPOINTS.fleetList(serviceType))
          .then((res: Response) => {
            if (isCancelled) return;
            return res.text();
          })
          .then((text: string | undefined) => {
            if (isCancelled || !text) return;
            
            // Parse CSV
            const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
            if (lines.length < 2) return;
            
            // ✅ Use proper CSV parser for headers (handles quoted values)
            const headers = parseCSVLine(lines[0]);
            const rows: Array<Record<string, string>> = [];
            
            for (let i = 1; i < lines.length; i++) {
              // ✅ Use proper CSV parser for data rows (handles quoted values)
              const values = parseCSVLine(lines[i]);
              const row: Record<string, string> = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              rows.push(row);
            }
            
            setFleetListCSV(rows);
            
            // Count vehicles by type
            const typeCounts = new Map<string, number>();
            const expectedTypes = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
            
            // Initialize counts
            expectedTypes.forEach(type => {
              typeCounts.set(type, 0);
            });
            
            rows.forEach(row => {
              // Get vehicle index to verify valid row
              let vehicleIndex = '';
              for (const key in row) {
                if (key.toUpperCase().trim() === 'VEHICLEINDEX') {
                  vehicleIndex = row[key] || '';
                  break;
                }
              }
              
              // Skip rows without valid VEHICLEINDEX
              const vehicleIndexTrimmed = vehicleIndex.trim();
              if (vehicleIndexTrimmed === '' || vehicleIndexTrimmed.toUpperCase() === 'VEHICLEINDEX') {
                return;
              }
              
              // Get vehicle type - ONLY use MAPPED VEHICLE TYPE (no fallback)
              let vehicleType = '';
              for (const key in row) {
                const upperKey = key.toUpperCase().trim();
                if (upperKey === 'MAPPED VEHICLE TYPE' || 
                    upperKey === 'MAPPEDVEHICLETYPE' || 
                    upperKey === 'MAPPED_VEHICLE_TYPE') {
                  vehicleType = row[key] || '';
                  break;
                }
              }
              
              // Skip rows without MAPPED VEHICLE TYPE
              if (!vehicleType || vehicleType.trim() === '') return;
              
              const trimmedType = vehicleType.trim();
              
              // Match to expected types (case-insensitive)
              const matchedType = expectedTypes.find(expectedType => 
                expectedType.toLowerCase() === trimmedType.toLowerCase()
              );
              
              if (matchedType) {
                typeCounts.set(matchedType, (typeCounts.get(matchedType) || 0) + 1);
              }
            });
            
            // Update vehicle quantities based on real counts
            setVehicleTypes(prevTypes => {
              const vehicleTypeMapping: Record<string, string> = {
                'Response car': 'response-car',
                'Small van': 'small-van',
                'Large van': 'large-van',
                'Pumping appliance': 'pumping-appliance',
                'Other appliance': 'other-appliance',
              };
              
              return prevTypes.map(vehicle => {
                const expectedTypeName = Object.entries(vehicleTypeMapping).find(
                  ([_, id]) => id === vehicle.id
                )?.[0];
                
                if (expectedTypeName) {
                  const realQuantity = typeCounts.get(expectedTypeName) || 0;
                  return {
                    ...vehicle,
                    quantity: realQuantity > 0 ? realQuantity : vehicle.quantity,
                  };
                }
                
                return vehicle;
              });
            });
          })
          .catch(err => {
            if (isCancelled) return;
            logger.error('Error loading fleet list:', err);
          });
      } else if (activeFleetSource === 'optifleet') {
        // Logistics/Optifleet only: use vehicle level CSV for counts and vehicleId→typeId mapping
        setVehicleIdToTypeId({});
        if (!selectedPeriod) return;
        
        authenticatedFetch(API_ENDPOINTS.vehicleLevelCSV(selectedPeriod as string, serviceType))
          .then((res: Response) => {
            if (isCancelled) return;
            return res.text();
          })
          .then((text: string | undefined) => {
            if (isCancelled || !text) return;
            
            // Parse CSV
            const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
            if (lines.length < 2) return;
            
            // ✅ Use proper CSV parser for headers (handles quoted values)
            const headers = parseCSVLine(lines[0]);
            const rows: Array<Record<string, string>> = [];
            
            for (let i = 1; i < lines.length; i++) {
              // ✅ Use proper CSV parser for data rows (handles quoted values)
              const values = parseCSVLine(lines[i]);
              const row: Record<string, string> = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              rows.push(row);
            }
            
            // Count unique vehicles by type and build vehicleId -> typeId map for profitability snapshot
            const dieselVehicles = new Set<string>();
            const electricVehicles = new Set<string>();
            const idToType: Record<string, string> = {};
            
            rows.forEach(row => {
              const vehicleId = row.Vehicle_ID || row.vehicle_id || '';
              if (!vehicleId || vehicleId.trim() === '') return;
              
              const vehicleType = row.Vehicle_Type || row.vehicle_type || '';
              if (!vehicleType || vehicleType.trim() === '') return;
              
              const trimmedType = vehicleType.trim().toLowerCase();
              const id = standardiseVehicleId(vehicleId);
              if (!id) return;
              
              // Match diesel or electric
              if (trimmedType.includes('electric') || trimmedType.includes('ev') || trimmedType.includes('e-')) {
                electricVehicles.add(id);
                idToType[id] = 'electric-truck';
              } else if (trimmedType.includes('diesel') || trimmedType.includes('truck')) {
                dieselVehicles.add(id);
                idToType[id] = 'diesel-truck';
              }
            });
            
            setVehicleIdToTypeId(idToType);
            
            // Update vehicle quantities based on real counts
            setVehicleTypes(prevTypes => {
              return prevTypes.map(vehicle => {
                if (vehicle.id === 'diesel-truck') {
                  return {
                    ...vehicle,
                    quantity: dieselVehicles.size > 0 ? dieselVehicles.size : vehicle.quantity,
                  };
                } else if (vehicle.id === 'electric-truck') {
                  return {
                    ...vehicle,
                    quantity: electricVehicles.size > 0 ? electricVehicles.size : vehicle.quantity,
                  };
                }
                return vehicle;
              });
            });
          })
          .catch(err => {
            if (isCancelled) return;
            logger.error('Error loading vehicle level CSV:', err);
          });
      }
    });
    
    return () => {
      isCancelled = true;
    };
  }, [serviceType, useCase, selectedPeriod, activeFleetSource]);

  // Fetch vehicle type CSV data to get real fuel costs
  useEffect(() => {
    if (!selectedPeriod) return;
    
    let isCancelled = false;
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      authenticatedFetch(API_ENDPOINTS.vehicleTypeLevelCSV(selectedPeriod as string, serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          return res.text();
        })
        .then((text: string | undefined) => {
          if (isCancelled || !text) return;
          
          // Parse CSV
          const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
          if (lines.length < 2) return;
          
          // ✅ Use proper CSV parser for headers (handles quoted values)
          const headers = parseCSVLine(lines[0]);
          const rows: Array<Record<string, string>> = [];
          
          for (let i = 1; i < lines.length; i++) {
            // ✅ Use proper CSV parser for data rows (handles quoted values)
            const values = parseCSVLine(lines[i]);
            const row: Record<string, string> = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            rows.push(row);
          }
          
          setVehicleTypeCSV(rows);
          
          // Map vehicle types from CSV to CostManage vehicle types (logistics canonical keys)
          const vehicleTypeMapping: Record<string, string> = {
            'Response car': 'response-car',
            'Small van': 'small-van',
            'Large van': 'large-van',
            'Pumping appliance': 'pumping-appliance',
            'Other appliance': 'other-appliance',
            'Diesel Truck': 'diesel-truck',
            'Electric Truck': 'electric-truck',
          };
          
          // Update fuel costs based on real data
          setVehicleTypes(prevTypes => {
            // Helper function to calculate annualization factor based on period format
            const getAnnualizationFactor = (period: string): number => {
              if (!period) {
                logger.warn('[CostManage] selectedPeriod is empty, using default factor of 12');
                return 12; // Default to monthly if period not available
              }
              
              // Check if period is a date range (YYYYMMDD_to_YYYYMMDD format)
              const dateRangeMatch = period.match(/(\d{8})_to_(\d{8})/);
              if (dateRangeMatch) {
                const startDateStr = dateRangeMatch[1];
                const endDateStr = dateRangeMatch[2];
                
                // Parse dates (YYYYMMDD format)
                const startDate = new Date(
                  parseInt(startDateStr.substring(0, 4)),
                  parseInt(startDateStr.substring(4, 6)) - 1,
                  parseInt(startDateStr.substring(6, 8))
                );
                const endDate = new Date(
                  parseInt(endDateStr.substring(0, 4)),
                  parseInt(endDateStr.substring(4, 6)) - 1,
                  parseInt(endDateStr.substring(6, 8))
                );
                
                // Calculate days in period
                const daysInPeriod = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
                const annualizationFactor = 365 / daysInPeriod;
                
                logger.debug(`[CostManage] Period: ${period}, Days: ${daysInPeriod}, Annualization Factor: ${annualizationFactor}`);
                
                // Annualize to full year (365 days)
                return annualizationFactor;
              }
              
              // Default to assuming monthly period (MONYY format like JUL24)
              logger.debug(`[CostManage] Period ${period} treated as monthly, using factor of 12`);
              return 12;
            };
            
            return prevTypes.map(vehicle => {
              const csvRow = rows.find(row => {
                const vehicleType = String(row.Vehicle_Type || row.vehicle_type || '').trim();
                if (!vehicleType) return false;
                if (serviceType === 'emergency_services') {
                  return mapRawLabelToEsCostManageTypeId(vehicleType) === vehicle.id;
                }
                const mappingKey = Object.keys(vehicleTypeMapping).find(
                  (k) => k.toLowerCase() === vehicleType.toLowerCase()
                );
                return mappingKey ? vehicleTypeMapping[mappingKey] === vehicle.id : false;
              });
              
              if (csvRow) {
                // For Electric Trucks, use electricity cost instead of fuel cost
                const isElectricTruck = vehicle.id === 'electric-truck';
                const totalFuelCost = isElectricTruck
                  ? parseFloat(csvRow.electricity_cost_GBP || csvRow.electricity_cost || '0')
                  : (parseFloat(csvRow.active_fuel_cost_GBP || csvRow.active_fuel_cost || '0') + 
                     parseFloat(csvRow.idle_fuel_cost_GBP || '0'));
                
                if (isElectricTruck) {
                  logger.debug(`[CostManage] Electric Truck CSV Row:`, {
                    Vehicle_Type: csvRow.Vehicle_Type,
                    electricity_cost_GBP: csvRow.electricity_cost_GBP,
                    electricity_cost: csvRow.electricity_cost,
                    totalFuelCost: totalFuelCost,
                    quantity: vehicle.quantity
                  });
                }
                
                // Prefer live fleet quantity but fall back to CSV-reported fleet size to avoid zeroing electric trucks before counts load
                const csvFleetQuantityRaw = csvRow.vehicles_dispatched || csvRow.Vehicles_Dispatched || csvRow.total_vehicles || csvRow.Total_Vehicles || csvRow.total || '';
                const csvFleetQuantity = Number.parseInt(csvFleetQuantityRaw, 10);
                const totalFleetQuantity = Math.max(vehicle.quantity, Number.isNaN(csvFleetQuantity) ? 0 : csvFleetQuantity);
                const safeFleetQuantity = totalFleetQuantity > 0 ? totalFleetQuantity : 1;
                
                // The CSV contains total fuel/electricity cost for the selected period
                // Calculate per-vehicle cost for the period based on total fleet size
                const periodFuelCostPerVehicle = totalFuelCost / safeFleetQuantity;
                
                // Annualize based on period type (monthly for Emergency Services, date range for Logistics)
                const annualizationFactor = getAnnualizationFactor(selectedPeriod as string);
                const yearlyFuelCostPerVehicle = periodFuelCostPerVehicle * annualizationFactor;
                
                return {
                  ...vehicle,
                  fuelCost: yearlyFuelCostPerVehicle > 0 ? yearlyFuelCostPerVehicle : vehicle.fuelCost, // Use real data if available, otherwise keep default
                };
              } else {
                // CSV row not found for this vehicle type
                if (vehicle.id === 'electric-truck') {
                  logger.warn(`[CostManage] Electric Truck CSV row not found. Available vehicle types in CSV:`, 
                    rows.map(r => r.Vehicle_Type || r.vehicle_type || 'UNKNOWN'));
                }
              }
              
              return vehicle;
            });
          });
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading vehicle type CSV:', err);
        });
    });
    
    return () => {
      isCancelled = true;
    };
  }, [selectedPeriod, serviceType, useCase]);

  // Update vehicle types when mode changes
  useEffect(() => {
    setVehicleTypes(defaultVehicleTypes);
    // Clear replacement projects when switching modes to avoid invalid references
    setReplacementProjects([]);
    setShowAddProject(false);
  }, [useCase, defaultVehicleTypes]);

  // Fetch Supatrak vehicle list (same as Configuration) when Supatrak source is selected
  // Uses AssetType from supatrak_vehicle_list_enriched.csv for vehicle type mapping
  useEffect(() => {
    if (activeFleetSource !== 'supatrak') return;
    let isCancelled = false;
    setSupatrakListLoading(true);
    setSupatrakListError(null);
    setVehicleIdToTypeId({}); // clear Optifleet map until Supatrak list loads
    import('../config/api').then(({ API_ENDPOINTS }) => {
      authenticatedFetch(API_ENDPOINTS.vehicleList(serviceType))
        .then((res) => res.json().catch(() => ({})))
        .then((json: { ok?: boolean; rows?: Record<string, string | number>[]; error?: unknown }) => {
          if (isCancelled) return;
          if (!json.ok || !Array.isArray(json.rows)) {
            setSupatrakListError(json && 'error' in json ? String(json.error) : 'Invalid response');
            setSupatrakVehicleTypes(defaultVehicleTypes);
            return;
          }
          const rows = json.rows as Record<string, string | number>[];
          const typeCounts = new Map<string, number>();
          const typeFuelCounts = new Map<string, Map<string, number>>();
          const slug = (s: string) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          const idToType: Record<string, string> = {};
          rows.forEach((row) => {
            const typeVal = row.type ?? row.Type ?? ''; // AssetType from CSV
            const t = String(typeVal).trim();
            if (!t || t === '—') return;
            typeCounts.set(t, (typeCounts.get(t) || 0) + 1);
            const fuelVal = row.fuel_type ?? row.fuel ?? row.Fuel ?? row['Fuel Type'] ?? '';
            const f = String(fuelVal).trim();
            if (f && f !== '—') {
              if (!typeFuelCounts.has(t)) typeFuelCounts.set(t, new Map());
              const fuelMap = typeFuelCounts.get(t)!;
              fuelMap.set(f, (fuelMap.get(f) || 0) + 1);
            }
            const vehicleReg = String(row.vehicle_reg ?? row.vehicleReg ?? row['Vehicle Reg'] ?? '').trim();
            if (vehicleReg) {
              const regKey = standardiseVehicleId(vehicleReg);
              idToType[regKey] =
                serviceType === 'emergency_services' ? mapRawLabelToEsCostManageTypeId(t) : slug(t);
            }
          });
          setVehicleIdToTypeId(idToType);
          const defaultCosts = defaultVehicleTypes[0] ?? {
            unitCost: 50000, serviceLife: 7, operationalCost: 3000, fuelCost: 4000,
            evCost: 50000, evOperationalCost: 2500, evChargingCost: 0.15, chargerCost: 8000,
          };
          if (serviceType === 'emergency_services') {
            const canonicalCounts = new Map<string, number>();
            const canonicalFuel = new Map<string, Map<string, number>>();
            const mergeFuel = (canonId: string, rawName: string) => {
              const src = typeFuelCounts.get(rawName);
              if (!src) return;
              if (!canonicalFuel.has(canonId)) canonicalFuel.set(canonId, new Map());
              const dest = canonicalFuel.get(canonId)!;
              src.forEach((n, k) => dest.set(k, (dest.get(k) || 0) + n));
            };
            typeCounts.forEach((qty, rawName) => {
              const canon = mapRawLabelToEsCostManageTypeId(rawName);
              canonicalCounts.set(canon, (canonicalCounts.get(canon) || 0) + qty);
              mergeFuel(canon, rawName);
            });
            const supatrakTypes: VehicleType[] = ES_COST_MANAGE_TYPE_ORDER.map((canonId) => {
              const quantity = canonicalCounts.get(canonId) ?? 0;
              const match = defaultVehicleTypes.find((d) => d.id === canonId);
              const base = match ?? defaultCosts;
              const fuelMap = canonicalFuel.get(canonId);
              const fuelTypeBreakdown = fuelMap && fuelMap.size > 0
                ? Object.fromEntries([...fuelMap.entries()].sort((a, b) => a[0].localeCompare(b[0])))
                : undefined;
              return {
                id: canonId,
                name: match?.name ?? canonId,
                quantity,
                unitCost: base.unitCost,
                serviceLife: base.serviceLife,
                operationalCost: base.operationalCost,
                fuelCost: base.fuelCost,
                evCost: base.evCost ?? base.unitCost,
                evOperationalCost: base.evOperationalCost ?? base.operationalCost,
                evChargingCost: base.evChargingCost ?? 0.15,
                chargerCost: base.chargerCost ?? 8000,
                fuelTypeBreakdown,
              };
            });
            setSupatrakVehicleTypes(supatrakTypes);
          } else {
            const supatrakTypes: VehicleType[] = Array.from(typeCounts.entries()).map(([name, quantity]) => {
              const id = slug(name);
              const match = defaultVehicleTypes.find((d) => d.name.toLowerCase() === name.toLowerCase());
              const base = match ?? defaultCosts;
              const fuelMap = typeFuelCounts.get(name);
              const fuelTypeBreakdown = fuelMap && fuelMap.size > 0
                ? Object.fromEntries([...fuelMap.entries()].sort((a, b) => a[0].localeCompare(b[0])))
                : undefined;
              return {
                id,
                name,
                quantity,
                unitCost: base.unitCost,
                serviceLife: base.serviceLife,
                operationalCost: base.operationalCost,
                fuelCost: base.fuelCost,
                evCost: base.evCost ?? base.unitCost,
                evOperationalCost: base.evOperationalCost ?? base.operationalCost,
                evChargingCost: base.evChargingCost ?? 0.15,
                chargerCost: base.chargerCost ?? 8000,
                fuelTypeBreakdown,
              };
            });
            setSupatrakVehicleTypes(supatrakTypes);
          }
        })
        .catch((err) => {
          if (!isCancelled) {
            setSupatrakListError(err instanceof Error ? err.message : 'Failed to load vehicle list');
            setSupatrakVehicleTypes(defaultVehicleTypes);
          }
        })
        .finally(() => {
          if (!isCancelled) setSupatrakListLoading(false);
        });
    });
    return () => { isCancelled = true; };
  }, [activeFleetSource, serviceType, defaultVehicleTypes]);

  const effectiveVehicleTypes = activeFleetSource === 'optifleet' ? vehicleTypes : supatrakVehicleTypes;

  const { snapshot: profitabilitySnapshot } = useProfitabilitySnapshot();
  const profitabilityByVehicleType = useMemo(() => {
    if (!profitabilitySnapshot?.byVehicle || Object.keys(vehicleIdToTypeId).length === 0) {
      return {} as Record<string, { revenue: number; totalCost: number }>;
    }
    const byType: Record<string, { revenue: number; totalCost: number }> = {};
    for (const [vehicleId, values] of Object.entries(profitabilitySnapshot.byVehicle)) {
      const stdId = standardiseVehicleId(vehicleId);
      const typeId = vehicleIdToTypeId[stdId] ?? vehicleIdToTypeId[vehicleId];
      if (!typeId) continue;
      if (!byType[typeId]) byType[typeId] = { revenue: 0, totalCost: 0 };
      byType[typeId].revenue += values.revenue;
      byType[typeId].totalCost += values.totalCost;
    }
    return byType;
  }, [profitabilitySnapshot, vehicleIdToTypeId]);

  // Full snapshot totals (matches Profitability Report) and unmapped amounts (vehicles not in vehicle list)
  // Vehicles that come only from Jigsaw are excluded from unmapped (don't need to be in vehicle list)
  const { snapshotTotals, unmapped } = useMemo(() => {
    if (!profitabilitySnapshot?.byVehicle) {
      return { 
        snapshotTotals: { revenue: 0, totalCost: 0 }, 
        unmapped: { revenue: 0, totalCost: 0, count: 0, vehicleIds: [] } 
      };
    }
    const jigsawOnlySet = new Set(
      (profitabilitySnapshot.jigsawOnlyVehicleIds ?? []).map((id) => standardiseVehicleId(id))
    );
    let totalRevenue = 0;
    let totalCost = 0;
    let unmappedRevenue = 0;
    let unmappedCost = 0;
    const unmappedVehicleIds: string[] = [];
    for (const [vehicleId, values] of Object.entries(profitabilitySnapshot.byVehicle)) {
      const stdId = standardiseVehicleId(vehicleId);
      const typeId = vehicleIdToTypeId[stdId] ?? vehicleIdToTypeId[vehicleId];
      const isJigsawOnly = jigsawOnlySet.has(stdId);
      totalRevenue += values.revenue;
      totalCost += values.totalCost;
      if (!typeId && !isJigsawOnly) {
        unmappedRevenue += values.revenue;
        unmappedCost += values.totalCost;
        unmappedVehicleIds.push(vehicleId);
      }
    }
    return {
      snapshotTotals: { revenue: totalRevenue, totalCost },
      unmapped: { 
        revenue: unmappedRevenue, 
        totalCost: unmappedCost, 
        count: unmappedVehicleIds.length,
        vehicleIds: unmappedVehicleIds.sort()
      },
    };
  }, [profitabilitySnapshot, vehicleIdToTypeId]);

  // Count of vehicles that appeared in the profitability report per vehicle type (for Active column)
  const activeCountByVehicleType = useMemo(() => {
    if (!profitabilitySnapshot?.byVehicle) return {} as Record<string, number>;
    const jigsawOnlySet = new Set(
      (profitabilitySnapshot.jigsawOnlyVehicleIds ?? []).map((id) => standardiseVehicleId(id))
    );
    const count: Record<string, number> = {};
    for (const [vehicleId] of Object.entries(profitabilitySnapshot.byVehicle)) {
      const stdId = standardiseVehicleId(vehicleId);
      if (jigsawOnlySet.has(stdId)) continue;
      const typeId = vehicleIdToTypeId[stdId] ?? vehicleIdToTypeId[vehicleId];
      if (!typeId) continue;
      count[typeId] = (count[typeId] ?? 0) + 1;
    }
    return count;
  }, [profitabilitySnapshot, vehicleIdToTypeId]);

  /** Fleet Size tooltip text: Diesel/Electric breakdown when available */
  const getFleetSizeTooltip = (vehicle: VehicleType): string => {
    const breakdown = vehicle.fuelTypeBreakdown;
    if (breakdown && Object.keys(breakdown).length > 0) {
      return Object.entries(breakdown)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([fuel, count]) => `${fuel}: ${count}`)
        .join(', ');
    }
    return '';
  };


  /** Util % for Current Fleet section only: static glow (no pulse), same green/amber/red as elsewhere. */
  const getUtilPercentIconClassCurrentFleet = (pct: number | null | undefined): string => {
    if (pct == null || Number.isNaN(pct)) return '';
    if (pct >= 80) return 'bg-success util-dot-glow-green';
    if (pct >= 50) return 'bg-warning util-dot-glow-amber';
    return 'bg-destructive util-dot-glow-red';
  };

  /** Period length in days (inclusive) from snapshot; 0 if missing. Used for annualisation. */
  const periodDays = useMemo(() => {
    const period = profitabilitySnapshot?.period;
    if (!period?.startDate || !period?.endDate) return 0;
    const start = new Date(period.startDate).getTime();
    const end = new Date(period.endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;
    return Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1);
  }, [profitabilitySnapshot?.period]);

  /** Factor to scale period values to annual: 365 / periodDays when periodDays > 0. */
  const annualisationFactor = periodDays > 0 ? 365 / periodDays : 0;

  const setEffectiveVehicleTypes = useCallback(
    (updater: (prev: VehicleType[]) => VehicleType[]) => {
      if (activeFleetSource === 'optifleet') setVehicleTypes(updater);
      else setSupatrakVehicleTypes(updater);
    },
    [activeFleetSource]
  );

  // Calculate TCO for a vehicle type (annualized): capital + operational + fuel. Does not include Annual Cost from profitability.
  const calculateTCO = (vehicle: VehicleType, isEV: boolean = false, years: number = vehicle.serviceLife) => {
    const capitalCost = isEV ? (vehicle.evCost || vehicle.unitCost) : vehicle.unitCost;
    const operationalCost = isEV ? (vehicle.evOperationalCost || vehicle.operationalCost) : vehicle.operationalCost;

    // Use the computed annual fuel/electricity cost when available; fall back to evChargingCost if fuelCost is missing
    const evEnergyCost = Number.isFinite(vehicle.fuelCost) ? vehicle.fuelCost : (vehicle.evChargingCost || 0);
    const fuelCost = isEV ? evEnergyCost : vehicle.fuelCost;
    
    // Annualized capital cost + annual operational and fuel/energy costs
    return (capitalCost / years) + operationalCost + fuelCost;
  };

  /**
   * TCO including Annual Cost from profitability when available.
   * When we have profitability data, Annual Cost already includes fuel (and order + driver cost),
   * so we use capital + operational only and add (annualised profitability cost / active). We do not
   * add config fuelCost to avoid double-counting fuel.
   * When we have no profitability data, we use full baseTCO (capital + operational + fuel).
   */
  const getTCOWithAnnualCost = (vehicle: VehicleType): number => {
    const active = activeCountByVehicleType[vehicle.id] ?? 0;
    const periodCost = profitabilityByVehicleType[vehicle.id]?.totalCost;
    if (active > 0 && periodCost != null && periodDays > 0 && annualisationFactor > 0) {
      const capitalCost = vehicle.unitCost + (vehicle.conversionCost ?? 0) + (vehicle.initialTaxesFees ?? 2000);
      const ins = typeof vehicle.insurance === 'number' ? vehicle.insurance : 4000;
      const rt = typeof vehicle.roadTax === 'number' ? vehicle.roadTax : 550;
      const ol = typeof vehicle.oLicense === 'number' ? vehicle.oLicense : 2000;
      const tel = (typeof vehicle.telematics === 'number' ? vehicle.telematics : 20) * 12;
      const cln = (typeof vehicle.cleaning === 'number' ? vehicle.cleaning : 100) * 12;
      const prk = (typeof vehicle.parking === 'number' ? vehicle.parking : 300) * 12;
      const prm = typeof vehicle.permits === 'number' ? vehicle.permits : 1000;
      const ohPct = typeof vehicle.overhead === 'number' ? vehicle.overhead : 8;
      const operationalCost = (ins + rt + ol + tel + cln + prk + prm) * (1 + ohPct / 100);
      const years = vehicle.serviceLife;
      const annualCostFromProfitability = periodCost * annualisationFactor;
      return (capitalCost / years) + operationalCost + (annualCostFromProfitability / active);
    }
    return calculateTCO(vehicle);
  };

  // Calculate TCO with future cost increases (customizable % per annum)
  const calculateTCOWithInflation = (vehicle: VehicleType, isEV: boolean = false, targetYear: number = START_YEAR) => {
    const yearsFromStart = targetYear - START_YEAR;
    const inflationFactor = Math.pow(1 + (annualIncreasePercent / 100), yearsFromStart);

    const baseTCO = calculateTCO(vehicle, isEV);
    return baseTCO * inflationFactor;
  };

  // Calculate total annual cost for a year
  const calculateAnnualCost = (year: number) => {
    let total = 0;
    const yearsFromStart = year - START_YEAR;
    const inflationFactor = yearsFromStart > 0 ? Math.pow(1 + (annualIncreasePercent / 100), yearsFromStart) : 1;
    
    // Calculate current fleet quantities after applying replacement projects up to this year
    const fleetQuantitiesByType = new Map<string, number>();
    effectiveVehicleTypes.forEach(vehicle => {
      let currentQuantity = vehicle.quantity;
      
      // Apply all replacement projects up to and including this year
      replacementProjects
        .filter(p => p.vehicleTypeId === vehicle.id && p.year <= year)
        .forEach(project => {
          currentQuantity += project.quantity;
        });
      
      fleetQuantitiesByType.set(vehicle.id, Math.max(0, currentQuantity));
    });
    
    // Current fleet operational costs (with inflation for future years)
    // Vehicles continue operating until replaced, so we use the adjusted quantities
    effectiveVehicleTypes.forEach(vehicle => {
      const currentQuantity = fleetQuantitiesByType.get(vehicle.id) || 0;
      if (currentQuantity > 0) {
        const operationalCost = vehicle.operationalCost * inflationFactor;
        const fuelCost = vehicle.fuelCost * inflationFactor;
        total += currentQuantity * (operationalCost + fuelCost);
      }
    });

    // Replacement project costs (with inflation for future years)
    // Only count capital costs for projects in this specific year
    replacementProjects.forEach(project => {
      if (project.year === year) {
        const vehicle = effectiveVehicleTypes.find(v => v.id === project.vehicleTypeId);
        if (vehicle && project.quantity > 0) {
          const baseUnitCost = project.isEV ? (vehicle.evCost || vehicle.unitCost) : 
                          project.isDiesel ? vehicle.unitCost : vehicle.unitCost;
          const inflatedUnitCost = baseUnitCost * inflationFactor;
          total += project.quantity * inflatedUnitCost;
        }
      }
    });

    // Infrastructure costs (with inflation for future years)
    infrastructureProjects.forEach(project => {
      if (project.year === year) {
        total += project.totalCost * inflationFactor;
      }
    });

    return total;
  };

  // Calculate costs for all plans to create comparison chart
  const getPlanComparisonData = useMemo(() => {
    if (plans.length < 2) return [];
    
    const allYears = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => START_YEAR + i);
    const series: LineChartSeries[] = [];
    
    plans.forEach(plan => {
      const planProjects = replacementProjects.filter(p => p.planId === plan.id);
      const data = allYears.map(year => {
        // Calculate cost for this plan's projects
        let total = 0;
        const yearsFromStart = year - START_YEAR;
        const inflationFactor = yearsFromStart > 0 ? Math.pow(1 + (annualIncreasePercent / 100), yearsFromStart) : 1;
        
        // Calculate current fleet quantities after applying replacement projects up to this year
        const fleetQuantitiesByType = new Map<string, number>();
        effectiveVehicleTypes.forEach(vehicle => {
          let currentQuantity = vehicle.quantity;
          planProjects
            .filter(p => p.vehicleTypeId === vehicle.id && p.year <= year)
            .forEach(project => {
              currentQuantity += project.quantity;
            });
          fleetQuantitiesByType.set(vehicle.id, Math.max(0, currentQuantity));
        });
        
        // Current fleet operational costs
        effectiveVehicleTypes.forEach(vehicle => {
          const currentQuantity = fleetQuantitiesByType.get(vehicle.id) || 0;
          if (currentQuantity > 0) {
            const operationalCost = vehicle.operationalCost * inflationFactor;
            const fuelCost = vehicle.fuelCost * inflationFactor;
            total += currentQuantity * (operationalCost + fuelCost);
          }
        });

        // Replacement project costs
        planProjects.forEach(project => {
          if (project.year === year) {
            const vehicle = effectiveVehicleTypes.find(v => v.id === project.vehicleTypeId);
            if (vehicle && project.quantity > 0) {
              const baseUnitCost = project.isEV ? (vehicle.evCost || vehicle.unitCost) : 
                              project.isDiesel ? vehicle.unitCost : vehicle.unitCost;
              const inflatedUnitCost = baseUnitCost * inflationFactor;
              total += project.quantity * inflatedUnitCost;
            }
          }
        });

        // Infrastructure costs
        infrastructureProjects.forEach(project => {
          if (project.year === year) {
            total += project.totalCost * inflationFactor;
          }
        });
        
        return {
          label: year.toString(),
          value: total,
        };
      });
      
      series.push({
        name: plan.name,
        data,
        color: plan.color,
      });
    });
    
    return series;
  }, [plans, replacementProjects, effectiveVehicleTypes, annualIncreasePercent, infrastructureProjects]);

// Scenario comparison: current fleet run as diesel vs fully electrified, cumulative TCO per year
const tcoScenarioComparison = useMemo(() => {
  const yearsList = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => START_YEAR + i);
  let dieselCum = 0;
  let electricCum = 0;
  const diesel: { label: string; value: number }[] = [];
  const electric: { label: string; value: number }[] = [];
  let crossoverYear: string | null = null;

  yearsList.forEach((year) => {
    const dieselAnnual = effectiveVehicleTypes.reduce(
      (sum, v) => sum + calculateTCOWithInflation(v, false, year) * v.quantity, 0);
    const electricAnnual = effectiveVehicleTypes.reduce(
      (sum, v) => sum + calculateTCOWithInflation(v, true, year) * v.quantity, 0);
    dieselCum += dieselAnnual;
    electricCum += electricAnnual;
    diesel.push({ label: String(year), value: Math.round(dieselCum) });
    electric.push({ label: String(year), value: Math.round(electricCum) });
    if (crossoverYear === null && electricCum <= dieselCum) crossoverYear = String(year);
  });

  const series: LineChartSeries[] = [
    { name: 'Diesel (current fleet)', data: diesel, color: STATUS_COLORS.warning },
    { name: 'Electric (fully electrified)', data: electric, color: STATUS_COLORS.success },
  ];
  return { series, crossoverYear };
}, [effectiveVehicleTypes, annualIncreasePercent]);

  // Get replacement schedule for a vehicle type (only projects, no natural replacements)
  const getReplacementSchedule = (vehicleTypeId: string) => {
    const schedule: { year: number; quantity: number; type: 'project' }[] = [];

    // Add planned projects only (from active plan)
    getActivePlanProjects
      .filter(p => p.vehicleTypeId === vehicleTypeId)
      .forEach(p => {
        schedule.push({ year: p.year, quantity: p.quantity, type: 'project' });
      });

    return schedule.sort((a, b) => a.year - b.year);
  };

  const allYears = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => START_YEAR + i);
  
  // Always show all years
  const years = allYears;

  const handleAddProject = (isDiesel: boolean, isEV: boolean, isRemoval: boolean) => {
    if (newProject.vehicleTypeId && newProject.year && newProject.quantity) {
      // Use active plan (should always exist since we initialize with Plan 1)
      const currentPlanId = activePlanId || plans[0]?.id;
      if (!activePlanId && plans.length > 0) {
        setActivePlanId(plans[0].id);
      }
      
      const project: ReplacementProject = {
        id: `project-${Date.now()}`,
        vehicleTypeId: newProject.vehicleTypeId!,
        year: newProject.year!,
        quantity: isRemoval ? -newProject.quantity! : newProject.quantity!,
        isEV: isEV,
        isDiesel: isDiesel,
        planId: currentPlanId || undefined,
      };
      setReplacementProjects([...replacementProjects, project]);
      setNewProject({ year: START_YEAR, quantity: 1, isEV: false, isDiesel: false });
      setShowAddProject(false);
    }
  };

  const handleCreatePlan = () => {
    const planNumber = plans.length + 1;
    const newPlan: Plan = {
      id: `plan-${Date.now()}`,
      name: `Plan ${planNumber}`,
      color: planColors[plans.length % planColors.length],
      isActive: false,
    };
    setPlans([...plans, newPlan]);
  };

  const handleDeletePlan = (planId: string) => {
    // Remove all projects associated with this plan
    setReplacementProjects(replacementProjects.filter(p => p.planId !== planId));
    // Remove the plan
    setPlans(plans.filter(p => p.id !== planId));
    // If this was the active plan, switch to first available plan or null
    if (activePlanId === planId) {
      const remainingPlans = plans.filter(p => p.id !== planId);
      setActivePlanId(remainingPlans.length > 0 ? remainingPlans[0].id : null);
    }
  };

  const handleSetActivePlan = (planId: string) => {
    setActivePlanId(planId);
    setPlans(plans.map(p => ({ ...p, isActive: p.id === planId })));
  };

  // Get projects for the active plan (or all projects if no plan system)
  const getActivePlanProjects = useMemo(() => {
    if (!activePlanId) {
      // If no plan system, return all projects
      return replacementProjects.filter(p => !p.planId);
    }
    return replacementProjects.filter(p => p.planId === activePlanId);
  }, [replacementProjects, activePlanId]);

  const handleDeleteProject = (id: string) => {
    setReplacementProjects(replacementProjects.filter(p => p.id !== id));
  };

  const handleEditProject = (project: ReplacementProject) => {
    setEditingProject(project.id);
    setEditProjectData({
      isDiesel: project.isDiesel,
      isEV: project.isEV,
      quantity: Math.abs(project.quantity),
      isRemoval: project.quantity < 0,
    });
  };

  const handleSaveProjectEdit = (projectId: string) => {
    if (!editProjectData) return;
    
    setReplacementProjects(prevProjects =>
      prevProjects.map(p => {
        if (p.id === projectId) {
          const newQuantity = editProjectData.isRemoval ? -editProjectData.quantity : editProjectData.quantity;
          
          return {
            ...p,
            isDiesel: editProjectData.isDiesel,
            isEV: editProjectData.isEV,
            quantity: newQuantity,
          };
        }
        return p;
      })
    );
    
    setEditingProject(null);
    setEditProjectData(null);
  };

  const handleCancelProjectEdit = () => {
    setEditingProject(null);
    setEditProjectData(null);
  };

  // Update getFutureFleetConfiguration to use active plan projects
  const getFutureFleetConfiguration = useMemo(() => {
    const futureFleet: VehicleType[] = effectiveVehicleTypes.map(vehicle => {
      // Start with current fleet quantity
      let futureQuantity = vehicle.quantity;

      // Apply all replacement projects for this vehicle type (from active plan)
      getActivePlanProjects
        .filter(p => p.vehicleTypeId === vehicle.id)
        .forEach(project => {
          futureQuantity += project.quantity;
        });

      // For Logistics mode, handle Diesel Truck and Electric Truck separately
      if (useCase === 'logistics') {
        if (vehicle.id === 'diesel-truck') {
          // Apply diesel projects to diesel truck
          const dieselProjects = getActivePlanProjects.filter(p => 
            (p.vehicleTypeId === 'diesel-truck' || p.vehicleTypeId === 'electric-truck') && p.isDiesel
          );
          futureQuantity = vehicle.quantity + dieselProjects.reduce((sum, p) => sum + p.quantity, 0);
        } else if (vehicle.id === 'electric-truck') {
          // Apply EV projects to electric truck
          const evProjects = getActivePlanProjects.filter(p => 
            (p.vehicleTypeId === 'diesel-truck' || p.vehicleTypeId === 'electric-truck') && p.isEV
          );
          futureQuantity = vehicle.quantity + evProjects.reduce((sum, p) => sum + p.quantity, 0);
        }
      }

      return {
        ...vehicle,
        quantity: Math.max(0, futureQuantity)
      };
    }).filter(vehicle => vehicle.quantity > 0); // Only show vehicles with quantity > 0

    return futureFleet;
  }, [effectiveVehicleTypes, getActivePlanProjects, useCase]);

  const handleDragStart = (e: React.DragEvent, projectId: string) => {
    setDraggedProjectId(projectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', projectId);
  };

  const handleDragOver = (e: React.DragEvent, year: number, vehicleTypeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverYear(year);
    setDragOverVehicleTypeId(vehicleTypeId);
  };

  const handleDragLeave = () => {
    setDragOverYear(null);
    setDragOverVehicleTypeId(null);
  };

  const handleDrop = (e: React.DragEvent, targetYear: number, targetVehicleTypeId: string) => {
    e.preventDefault();
    setDragOverYear(null);
    setDragOverVehicleTypeId(null);
    
    if (!draggedProjectId) return;
    
    const project = replacementProjects.find(p => p.id === draggedProjectId);
    if (!project) return;
    
    // Update the project's year and optionally vehicle type
    setReplacementProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === draggedProjectId
          ? { ...p, year: targetYear, vehicleTypeId: targetVehicleTypeId }
          : p
      )
    );
    
    setDraggedProjectId(null);
  };

  const handleDragEnd = () => {
    setDraggedProjectId(null);
    setDragOverYear(null);
    setDragOverVehicleTypeId(null);
  };


  const handleCapitalCostFieldEdit = (vehicleId: string, field: string, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;
    setEffectiveVehicleTypes(prevTypes =>
      prevTypes.map(v =>
        v.id === vehicleId ? { ...v, [field]: numValue } : v
      )
    );
    setEditingCapitalCostField(null);
  };

  const handleOperationalCostFieldEdit = (
    vehicleId: string | string[],
    field: string,
    value: string
  ) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;
    const ids = Array.isArray(vehicleId) ? vehicleId : [vehicleId];
    setEffectiveVehicleTypes(prevTypes =>
      prevTypes.map(v => (ids.includes(v.id) ? { ...v, [field]: numValue } : v))
    );
    setEditingOperationalCostField(null);
  };


  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Calculator className="w-8 h-8 text-chart-5" />
            Cost Manage
          </h2>
          <p className="text-muted-foreground text-sm">
            Plan fleet electrification, replacement schedules, and analyse total cost of ownership (TCO) over time.
          </p>
        </div>
        {activeFleetSource === 'supatrak' ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              Period: {profitabilitySnapshot?.period?.startDate && profitabilitySnapshot?.period?.endDate
                ? `${profitabilitySnapshot.period.startDate} – ${profitabilitySnapshot.period.endDate}`
                : '—'}
            </span>
          </div>
        ) : (
          <PeriodSelector />
        )}
      </div>

      {/* TCO scenario comparison — diesel vs electrified */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
        <h3 className="text-lg font-semibold text-white mb-1">Cumulative TCO — current fleet vs electrified</h3>
        <p className="text-xs text-muted-foreground mb-4">
          {tcoScenarioComparison.crossoverYear
            ? `Electrification breaks even in ${tcoScenarioComparison.crossoverYear}.`
            : 'Same fleet size and composition, compared under diesel and electric cost assumptions.'}
        </p>
        <LineChart
          series={tcoScenarioComparison.series}
          yAxisLabel="Cumulative TCO (£)"
          formatYAxisAsK
          gradientFillSeriesName="Electric (fully electrified)"
          hideDataPoints
          chartHeight={320}
          heightClass="min-h-95"
          selectedXLabel={tcoScenarioComparison.crossoverYear}
        />
      </div>

      {/* Capital Costs Reference Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all p-6">
        <h3 className="text-lg font-semibold text-success mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success"></span>
          Capital Costs
        </h3>
        <div className="overflow-x-hidden">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-foreground font-medium border-r border-border">Type</th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help" title="Initial vehicle cost (one-time)">
                  <span className="group-hover:text-success transition-colors">Unit Cost (£)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Unit Purchase Price</p>
                    <p>Initial vehicle cost. One-time purchase expense.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help" title="First registration and taxes">
                  <span className="group-hover:text-success transition-colors">Initial Taxes/Fees (£)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Initial Taxes/Fees</p>
                    <p>First registration, import duties. One-time: £1,000-£3,000.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help" title="For EV conversions or modifications">
                  <span className="group-hover:text-success transition-colors">Conversion Costs (£)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Conversion Costs</p>
                    <p>For EV conversions or vehicle modifications. If applicable: £5,000-£15,000.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help" title="Years before replacement">
                  <span className="group-hover:text-success transition-colors">Useful Life (yr)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Useful Life</p>
                    <p>Years before replacement. Typical: 8-10 years (HGV), 5-7 years (van).</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help" title="Estimated value at end of life">
                  <span className="group-hover:text-success transition-colors">Residual Value (%)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Residual Value</p>
                    <p>Estimated value at end of life. Typical: 10-20% of purchase price.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-success font-semibold border-l border-border">Total (£/veh)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {effectiveVehicleTypes.map((vehicle) => {
                const isEditingUnitCost = editingCapitalCostField?.vehicleId === vehicle.id && editingCapitalCostField?.field === 'unitCost';
                const isEditingServiceLife = editingCapitalCostField?.vehicleId === vehicle.id && editingCapitalCostField?.field === 'serviceLife';
                const isEditingResidualValue = editingCapitalCostField?.vehicleId === vehicle.id && editingCapitalCostField?.field === 'residualValue';
                const isEditingConversionCost = editingCapitalCostField?.vehicleId === vehicle.id && editingCapitalCostField?.field === 'conversionCost';
                const isEditingInitialTaxesFees = editingCapitalCostField?.vehicleId === vehicle.id && editingCapitalCostField?.field === 'initialTaxesFees';

                const pencilCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, text`;

                const renderCapitalCell = (
                  field: CostManageNumericField,
                  defaultVal: number,
                  displayFn: (v: number) => string,
                  isEditing: boolean,
                  step: number = 1,
                  inputWidth: string = 'w-20'
                ) => {
                  const currentVal = vehicle[field] ?? defaultVal;
                  return (
                    <td
                      className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all hover:drop-shadow-glow-warning-lg group/edit"
                      onClick={() => !isEditing && setEditingCapitalCostField({ vehicleId: vehicle.id, field })}
                      style={{ display: 'table-cell', verticalAlign: 'middle', cursor: pencilCursor }}
                    >
                      {isEditing ? (
                        <input
                          type="number"
                          step={step}
                          defaultValue={currentVal}
                          onBlur={(e) => handleCapitalCostFieldEdit(vehicle.id, field, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCapitalCostFieldEdit(vehicle.id, field, (e.target as HTMLInputElement).value);
                            else if (e.key === 'Escape') setEditingCapitalCostField(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={`${inputWidth} px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border`}
                          autoFocus
                        />
                      ) : (
                        <span>{displayFn(currentVal)}</span>
                      )}
                    </td>
                  );
                };

                return (
                  <tr key={vehicle.id} className="hover:bg-secondary/50 transition-colors group cursor-pointer">
                    <td className="py-3 px-4 text-foreground font-medium border-r border-border group-hover:text-success transition-all">{vehicle.name}</td>
                    <td
                      className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all hover:drop-shadow-glow-warning-lg group/edit"
                      onClick={() => !isEditingUnitCost && setEditingCapitalCostField({ vehicleId: vehicle.id, field: 'unitCost' })}
                      style={{ display: 'table-cell', verticalAlign: 'middle', cursor: pencilCursor }}
                    >
                      {isEditingUnitCost ? (
                        <input
                          type="number"
                          step="1000"
                          defaultValue={vehicle.unitCost}
                          onBlur={(e) => handleCapitalCostFieldEdit(vehicle.id, 'unitCost', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCapitalCostFieldEdit(vehicle.id, 'unitCost', (e.target as HTMLInputElement).value);
                            else if (e.key === 'Escape') setEditingCapitalCostField(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-24 px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border"
                          autoFocus
                        />
                      ) : (
                        <span>£{(vehicle.unitCost / 1000).toFixed(1)}k</span>
                      )}
                    </td>
                    {/* Unit Cost - already first */}
                    {renderCapitalCell('initialTaxesFees', 2000, (v) => `£${(v/1000).toFixed(1)}k`, isEditingInitialTaxesFees, 100, 'w-20')}
                    {renderCapitalCell('conversionCost', 0, (v) => v === 0 ? '—' : `£${(v/1000).toFixed(1)}k`, isEditingConversionCost, 500, 'w-20')}
                    <td
                      className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all hover:drop-shadow-glow-warning-lg group/edit"
                      onClick={() => !isEditingServiceLife && setEditingCapitalCostField({ vehicleId: vehicle.id, field: 'serviceLife' })}
                      style={{ display: 'table-cell', verticalAlign: 'middle', cursor: pencilCursor }}
                    >
                      {isEditingServiceLife ? (
                        <input
                          type="number"
                          defaultValue={vehicle.serviceLife}
                          onBlur={(e) => handleCapitalCostFieldEdit(vehicle.id, 'serviceLife', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCapitalCostFieldEdit(vehicle.id, 'serviceLife', (e.target as HTMLInputElement).value);
                            else if (e.key === 'Escape') setEditingCapitalCostField(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border"
                          autoFocus
                        />
                      ) : (
                        <span>{vehicle.serviceLife}</span>
                      )}
                    </td>
                    {renderCapitalCell('residualValue', 15, (v) => `${v}%`, isEditingResidualValue, 1, 'w-16')}
                    <td className="py-3 px-4 text-right text-success font-semibold border-l border-border group-hover:text-success transition-all">
                      £{((vehicle.unitCost + (vehicle.conversionCost ?? 0) + (vehicle.initialTaxesFees ?? 2000)) / 1000).toFixed(1)}k
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Operational Costs Reference Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all p-6">
        <h3 className="text-lg font-semibold text-success mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success"></span>
          Operational Costs
        </h3>
        <div className="overflow-x-hidden">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-foreground font-medium border-r border-border">Type</th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Insurance (£/yr)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Insurance</p>
                    <p>Commercial vehicle insurance. Typical: £3,000-£5,000/yr (HGV).</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Road Tax (£/yr)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Road Tax (VED)</p>
                    <p>Vehicle Excise Duty. Depends on weight/emissions: £165-£1,850/yr.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">O-License (£/yr)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Operator License</p>
                    <p>O-license fees. Depends on fleet size: £400-£8,000/yr.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Telematics (£/mo)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Telematics</p>
                    <p>GPS tracking, fleet management software. Typical: £15-£30/vehicle/month.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Cleaning (£/mo)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Cleaning/Valeting</p>
                    <p>Regular vehicle cleaning. Typical: £50-£200/month.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Parking (£/mo)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Parking/Depot</p>
                    <p>Depot rent, parking fees. Typical: £100-£500/vehicle/month.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Permits (£/yr)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Permits</p>
                    <p>Congestion charges, LEZ charges, etc. Typical: £500-£2,000/yr (London).</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium group relative cursor-help">
                  <span className="group-hover:text-success transition-colors">Overhead (%)</span>
                  <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 bg-popover text-foreground text-xs rounded p-3 z-10 border border-border">
                    <p className="font-semibold text-success mb-1">Admin/Overhead</p>
                    <p>Fleet admin, compliance, back-office. Typical: 5-10% of operational costs.</p>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-success font-semibold border-l border-border">Total (£/yr/veh)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
  {(() => {
    const opDefaultsSig = { insurance: 4000, roadTax: 550, oLicense: 2000, telematics: 20, cleaning: 100, parking: 300, permits: 1000, overhead: 8 };
    const signature = (v: VehicleType) =>
      (Object.keys(opDefaultsSig) as (keyof typeof opDefaultsSig)[])
        .map(k => (typeof v[k] === 'number' ? v[k] : opDefaultsSig[k]))
        .join('|');

    const groups = Object.values(
      effectiveVehicleTypes.reduce<Record<string, VehicleType[]>>((acc, v) => {
        (acc[signature(v)] ??= []).push(v);
        return acc;
      }, {})
    );

    return groups.map((group) => {
      const vehicle = group[0];                 // representative for display/defaults
      const groupIds = group.map(g => g.id);
      const rowLabel = group.map(g => g.name).join(' / ');

      const isEditingInsurance = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'insurance';
      const isEditingRoadTax = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'roadTax';
      const isEditingOLicense = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'oLicense';
      const isEditingTelematics = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'telematics';
      const isEditingCleaning = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'cleaning';
      const isEditingParking = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'parking';
      const isEditingPermits = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'permits';
      const isEditingOverhead = editingOperationalCostField?.vehicleId === vehicle.id && editingOperationalCostField?.field === 'overhead';

      const handleClick = (field: string) => {
        setEditingOperationalCostField({ vehicleId: vehicle.id, field });
      };

      const opDefaults: Record<string, number> = {
        insurance: 4000, roadTax: 550, oLicense: 2000, telematics: 20,
        cleaning: 100, parking: 300, permits: 1000, overhead: 8,
      };
      const opDisplayDefaults: Record<string, string> = {
        insurance: '£4k', roadTax: '£550', oLicense: '£2k', telematics: '£20',
        cleaning: '£100', parking: '£300', permits: '£1k', overhead: '8%',
      };

      const renderEditableCell = (field: OperationalCostField, isEditing: boolean) => {
        const savedVal = vehicle[field];
        const numericDefault = opDefaults[field] ?? 0;
        const currentNumeric = typeof savedVal === 'number' ? savedVal : numericDefault;
        const displayStr = typeof savedVal === 'number'
          ? (field === 'overhead' ? `${savedVal}%` : savedVal >= 1000 ? `£${(savedVal/1000).toFixed(1)}k` : `£${savedVal}`)
          : (opDisplayDefaults[field] ?? String(numericDefault));
        return (
          <td
            className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all hover:drop-shadow-glow-warning-lg group/edit"
            onClick={() => !isEditing && handleClick(field)}
            style={{ display: 'table-cell', verticalAlign: 'middle', cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, text` }}
          >
            {isEditing ? (
              <input
                type="number"
                step={field === 'overhead' ? '1' : '100'}
                defaultValue={currentNumeric}
                onBlur={(e) => handleOperationalCostFieldEdit(groupIds, field, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleOperationalCostFieldEdit(groupIds, field, (e.target as HTMLInputElement).value);
                  else if (e.key === 'Escape') setEditingOperationalCostField(null);
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-20 px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border"
                autoFocus
              />
            ) : (
              <span>{displayStr}</span>
            )}
          </td>
        );
      };

      return (
        <tr key={vehicle.id} className="hover:bg-secondary/50 transition-colors group cursor-pointer">
          <td className="py-3 px-4 text-foreground font-medium border-r border-border group-hover:text-success transition-all">{rowLabel}</td>
          {renderEditableCell('insurance', isEditingInsurance)}
          {renderEditableCell('roadTax', isEditingRoadTax)}
          {renderEditableCell('oLicense', isEditingOLicense)}
          {renderEditableCell('telematics', isEditingTelematics)}
          {renderEditableCell('cleaning', isEditingCleaning)}
          {renderEditableCell('parking', isEditingParking)}
          {renderEditableCell('permits', isEditingPermits)}
          {renderEditableCell('overhead', isEditingOverhead)}
          <td className="py-3 px-4 text-right text-success font-semibold border-l border-border group-hover:text-success transition-all">
            {(() => {
              const ins = typeof vehicle.insurance === 'number' ? vehicle.insurance : 4000;
              const rt = typeof vehicle.roadTax === 'number' ? vehicle.roadTax : 550;
              const ol = typeof vehicle.oLicense === 'number' ? vehicle.oLicense : 2000;
              const tel = (typeof vehicle.telematics === 'number' ? vehicle.telematics : 20) * 12;
              const cln = (typeof vehicle.cleaning === 'number' ? vehicle.cleaning : 100) * 12;
              const prk = (typeof vehicle.parking === 'number' ? vehicle.parking : 300) * 12;
              const prm = typeof vehicle.permits === 'number' ? vehicle.permits : 1000;
              const ohPct = typeof vehicle.overhead === 'number' ? vehicle.overhead : 8;
              const subtotal = ins + rt + ol + tel + cln + prk + prm;
              const total = subtotal * (1 + ohPct / 100);
              return total >= 1000000
                ? `£${(total / 1000000).toFixed(1)}m`
                : `£${(total / 1000).toFixed(1)}k`;
            })()}
          </td>
        </tr>
      );
    });
  })()}
</tbody>
          </table>
        </div>
      </div>

      {/* Fleet Configuration Section - same for Supatrak and Optifleet */}
      <div className="bg-card rounded-lg shadow-sm border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {/* Current Fleet Button - static green glow when active (no pulse) */}
            <Button
              variant={activeFleetView === 'current' ? 'success' : 'secondary'} size="md" type="button"
              onClick={() => setActiveFleetView('current')}
              className={`border ${
                activeFleetView === 'current'
                  ? 'bg-success/30 text-success border-success drop-shadow-glow-success-md'
                  : 'border-border'
              }`}
            >
              Current Fleet
            </Button>
            {/* Future Fleet Button */}
            <Button
              variant={activeFleetView === 'future' ? 'success' : 'secondary'} size="md" type="button"
              onClick={() => setActiveFleetView('future')}
              className={`border ${
                activeFleetView === 'future'
                  ? 'bg-success/30 text-success border-success drop-shadow-glow-success-md'
                  : 'border-border'
              }`}
            >
              Future Fleet
            </Button>
          </div>
          <Button variant="secondary" size="sm" type="button" className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50">
            <Download aria-hidden className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
            <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
          </Button>
        </div>
        
        {/* Current Plan Indicator - shown for Future Fleet */}
        {activeFleetView === 'future' && (
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
            <p className="text-xs text-muted-foreground">Current Plan:</p>
            {activePlanId ? (
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: plans.find(p => p.id === activePlanId)?.color || planColors[0] }}
                />
                <span className="text-xs text-success font-medium">
                  {plans.find(p => p.id === activePlanId)?.name || 'Plan 1'}
                </span>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">No plan selected</span>
            )}
          </div>
        )}

        {activeFleetSource === 'supatrak' && supatrakListError && (
          <p className="text-warning text-sm mb-4" role="alert">
            {supatrakListError} Upload the vehicle list to S3 (e.g. via BackEnd/upload_file_to_s3.py) to see Supatrak fleet data.
          </p>
        )}
        {activeFleetSource === 'supatrak' && supatrakListLoading && effectiveVehicleTypes.length === 0 && (
          <p className="text-muted-foreground text-sm mb-4">Loading Supatrak vehicle list…</p>
        )}

          {/* Current Fleet Table */}
          {activeFleetView === 'current' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-foreground font-medium border-r border-border hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Type</th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer" title="Active / Fleet Size">Active/Fleet</th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Util %</th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer w-20">
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Capital Costs (£/veh)</span>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer w-20">
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Op. Cost (£/yr/veh)</span>
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer annual-cost-column-glow-static">Annual Cost (£)</th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">TCO (£/yr)</th>
                <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer annual-revenue-column-glow-static">Annual Revenue (£)</th>
              </tr>
            </thead>
            <tbody>
              {effectiveVehicleTypes.map((vehicle) => {
                return (
                  <tr key={vehicle.id} className="border-b border-border/50 hover:bg-secondary/30 transition-all cursor-pointer group">
                    <td className="py-3 px-4 text-white font-medium border-r border-border group-hover:text-success transition-all">{redactVehicleType(vehicle.name, isDemoMode)}</td>
                    <td
                      className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all"
                      title={getFleetSizeTooltip(vehicle) || undefined}
                    >
                      {profitabilitySnapshot?.byVehicle
                        ? `${activeCountByVehicleType[vehicle.id] ?? 0}/${vehicle.quantity}`
                        : `${vehicle.quantity}`}
                    </td>
                    <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all">
                      {vehicle.quantity > 0 && profitabilitySnapshot?.byVehicle ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <span
                            className={`inline-block w-2 h-2 rounded-full shrink-0 ${getUtilPercentIconClassCurrentFleet(Math.round(((activeCountByVehicleType[vehicle.id] ?? 0) / vehicle.quantity) * 100))}`}
                            aria-hidden
                          />
                          <span>{Math.round(((activeCountByVehicleType[vehicle.id] ?? 0) / vehicle.quantity) * 100)}%</span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all w-20">
                      <span>{(vehicle.unitCost + (vehicle.conversionCost ?? 0) + (vehicle.initialTaxesFees ?? 2000)).toLocaleString()}</span>
                    </td>
                    <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all w-20">
                      {(() => {
                        const ins = typeof vehicle.insurance === 'number' ? vehicle.insurance : 4000;
                        const rt = typeof vehicle.roadTax === 'number' ? vehicle.roadTax : 550;
                        const ol = typeof vehicle.oLicense === 'number' ? vehicle.oLicense : 2000;
                        const tel = (typeof vehicle.telematics === 'number' ? vehicle.telematics : 20) * 12;
                        const cln = (typeof vehicle.cleaning === 'number' ? vehicle.cleaning : 100) * 12;
                        const prk = (typeof vehicle.parking === 'number' ? vehicle.parking : 300) * 12;
                        const prm = typeof vehicle.permits === 'number' ? vehicle.permits : 1000;
                        const ohPct = typeof vehicle.overhead === 'number' ? vehicle.overhead : 8;
                        return Math.round((ins + rt + ol + tel + cln + prk + prm) * (1 + ohPct / 100)).toLocaleString();
                      })()}
                    </td>
                    <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all annual-cost-column-glow-static">
                      {profitabilityByVehicleType[vehicle.id] != null && periodDays > 0 ? (
                        <span
                          className="inline-block group/cost cursor-help"
                          onMouseMove={(e) => {
                            const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                            if (tooltip && tooltip.style.display !== 'none') {
                              const rect = e.currentTarget.getBoundingClientRect();
                              tooltip.style.left = `${rect.left + rect.width / 2}px`;
                              tooltip.style.top = `${rect.top - 8}px`;
                            }
                          }}
                        >
                          {Math.round(profitabilityByVehicleType[vehicle.id].totalCost * annualisationFactor).toLocaleString()}
                          <span className="tooltip-content z-float hidden group-hover/cost:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                            <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Cost</div>
                            <div className="px-4 py-3 space-y-2 text-center">
                              <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                              <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(profitabilityByVehicleType[vehicle.id].totalCost).toLocaleString()}</span></div>
                              <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(profitabilityByVehicleType[vehicle.id].totalCost * annualisationFactor).toLocaleString()}</span></div>
                            </div>
                          </span>
                        </span>
                      ) : profitabilityByVehicleType[vehicle.id] != null ? (
                        Math.round(profitabilityByVehicleType[vehicle.id].totalCost).toLocaleString()
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-success font-semibold group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">
                      {(() => {
                        const tcoValue = getTCOWithAnnualCost(vehicle);
                        return (
                          <span
                            className="inline-block cursor-help"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTcoTooltip({
                                left: rect.left + rect.width / 2,
                                top: rect.top - 8,
                                vehicleId: vehicle.id,
                                isTotal: false
                              });
                            }}
                            onMouseLeave={() => setTcoTooltip(null)}
                          >
                            {tcoValue.toFixed(0).toLocaleString()}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all annual-revenue-column-glow-static">
                      {profitabilityByVehicleType[vehicle.id] != null && periodDays > 0 ? (
                        <span
                          className="inline-block group/revenue cursor-help"
                          onMouseMove={(e) => {
                            const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                            if (tooltip && tooltip.style.display !== 'none') {
                              const rect = e.currentTarget.getBoundingClientRect();
                              tooltip.style.left = `${rect.left + rect.width / 2}px`;
                              tooltip.style.top = `${rect.top - 8}px`;
                            }
                          }}
                        >
                          {Math.round(profitabilityByVehicleType[vehicle.id].revenue * annualisationFactor).toLocaleString()}
                          <span className="tooltip-content z-float hidden group-hover/revenue:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                            <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Revenue</div>
                            <div className="px-4 py-3 space-y-2 text-center">
                              <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                              <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(profitabilityByVehicleType[vehicle.id].revenue).toLocaleString()}</span></div>
                              <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(profitabilityByVehicleType[vehicle.id].revenue * annualisationFactor).toLocaleString()}</span></div>
                            </div>
                          </span>
                        </span>
                      ) : profitabilityByVehicleType[vehicle.id] != null ? (
                        Math.round(profitabilityByVehicleType[vehicle.id].revenue).toLocaleString()
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
              {(unmapped.revenue > 0 || unmapped.totalCost > 0) && (
                <tr className="border-b border-border/50 bg-card/50">
                  <td className="py-3 px-4 text-muted-foreground italic border-r border-border" title="Revenue and costs from vehicles that are not in your vehicle list (Supatrak or Optifleet). Add them to the list to assign a type.">Vehicles not in Supatrak</td>
                  <td 
                    className="py-3 px-4 text-right text-foreground cursor-pointer hover:text-success transition-all"
                    onClick={() => setShowUnmappedVehiclesModal(true)}
                    title="Click to view unmapped vehicle registrations"
                  >
                    {unmapped.count > 0 ? `${unmapped.count}/${unmapped.count}` : '—'}
                  </td>
                  <td className="py-3 px-4 text-right text-muted-foreground">—</td>
                  <td className="py-3 px-4 text-right text-muted-foreground">—</td>
                  <td className="py-3 px-4 text-right text-muted-foreground">—</td>
                  <td className="py-3 px-4 text-right text-muted-foreground annual-cost-column-glow-static">
                    {unmapped.totalCost > 0 && periodDays > 0 ? (
                      <span
                        className="inline-block group/cost cursor-help"
                        onMouseMove={(e) => {
                          const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                          if (tooltip && tooltip.style.display !== 'none') {
                            const rect = e.currentTarget.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width / 2}px`;
                            tooltip.style.top = `${rect.top - 8}px`;
                          }
                        }}
                      >
                        {Math.round(unmapped.totalCost * annualisationFactor).toLocaleString()}
                        <span className="tooltip-content z-float hidden group-hover/cost:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                          <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Cost (Unmapped)</div>
                          <div className="px-4 py-3 space-y-2 text-center">
                            <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                            <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(unmapped.totalCost).toLocaleString()}</span></div>
                            <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(unmapped.totalCost * annualisationFactor).toLocaleString()}</span></div>
                          </div>
                        </span>
                      </span>
                    ) : (
                      Math.round(unmapped.totalCost).toLocaleString()
                    )}
                  </td>
                  <td className="py-3 px-4 text-right text-muted-foreground">—</td>
                  <td className="py-3 px-4 text-right text-muted-foreground annual-revenue-column-glow-static">
                    {unmapped.revenue > 0 && periodDays > 0 ? (
                      <span
                        className="inline-block group/revenue cursor-help"
                        onMouseMove={(e) => {
                          const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                          if (tooltip && tooltip.style.display !== 'none') {
                            const rect = e.currentTarget.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width / 2}px`;
                            tooltip.style.top = `${rect.top - 8}px`;
                          }
                        }}
                      >
                        {Math.round(unmapped.revenue * annualisationFactor).toLocaleString()}
                        <span className="tooltip-content z-float hidden group-hover/revenue:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                          <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Revenue (Unmapped)</div>
                          <div className="px-4 py-3 space-y-2 text-center">
                            <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                            <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(unmapped.revenue).toLocaleString()}</span></div>
                            <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(unmapped.revenue * annualisationFactor).toLocaleString()}</span></div>
                          </div>
                        </span>
                      </span>
                    ) : (
                      Math.round(unmapped.revenue).toLocaleString()
                    )}
                  </td>
                </tr>
              )}
              <tr className="border-t-2 border-border font-semibold">
                <td className="py-3 px-4 text-white border-r border-border">TOTAL</td>
                <td className="py-3 px-4 text-right text-white">
                  {(() => {
                    const totalFleet = effectiveVehicleTypes.reduce((sum, v) => sum + v.quantity, 0);
                    const totalActive = effectiveVehicleTypes.reduce((sum, v) => sum + (activeCountByVehicleType[v.id] ?? 0), 0);
                    return profitabilitySnapshot?.byVehicle ? `${totalActive}/${totalFleet}` : String(totalFleet);
                  })()}
                </td>
                <td className="py-3 px-4 text-right text-white">
                  {(() => {
                    const totalFleet = effectiveVehicleTypes.reduce((sum, v) => sum + v.quantity, 0);
                    const totalActive = effectiveVehicleTypes.reduce((sum, v) => sum + (activeCountByVehicleType[v.id] ?? 0), 0);
                    const pct = totalFleet > 0 ? Math.round((totalActive / totalFleet) * 100) : null;
                    const iconClass = getUtilPercentIconClassCurrentFleet(pct);
                    return totalFleet > 0 ? (
                      <div className="flex items-center justify-end gap-1.5">
                        {iconClass ? (
                          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${iconClass}`} aria-hidden />
                        ) : null}
                        <span>{Math.round((totalActive / totalFleet) * 100)}%</span>
                      </div>
                    ) : (
                      '—'
                    );
                  })()}
                </td>
                <td className="py-3 px-4 text-right text-white">
                  {effectiveVehicleTypes.reduce((sum, v) => sum + (v.unitCost + (v.conversionCost ?? 0) + (v.initialTaxesFees ?? 2000)), 0).toLocaleString()}
                </td>
                <td className="py-3 px-4 text-right text-white">
                  {effectiveVehicleTypes.reduce((sum, v) => {
                    const ins = typeof v.insurance === 'number' ? v.insurance : 4000;
                    const rt = typeof v.roadTax === 'number' ? v.roadTax : 550;
                    const ol = typeof v.oLicense === 'number' ? v.oLicense : 2000;
                    const tel = (typeof v.telematics === 'number' ? v.telematics : 20) * 12;
                    const cln = (typeof v.cleaning === 'number' ? v.cleaning : 100) * 12;
                    const prk = (typeof v.parking === 'number' ? v.parking : 300) * 12;
                    const prm = typeof v.permits === 'number' ? v.permits : 1000;
                    const ohPct = typeof v.overhead === 'number' ? v.overhead : 8;
                    return sum + Math.round((ins + rt + ol + tel + cln + prk + prm) * (1 + ohPct / 100));
                  }, 0).toLocaleString()}
                </td>
                <td className="py-3 px-4 text-right text-white annual-cost-column-glow-static">
                  {profitabilitySnapshot?.byVehicle && Object.keys(profitabilitySnapshot.byVehicle).length > 0 && periodDays > 0 ? (
                    <span
                      className="inline-block group/cost cursor-help"
                      onMouseMove={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                        if (tooltip && tooltip.style.display !== 'none') {
                          const rect = e.currentTarget.getBoundingClientRect();
                          tooltip.style.left = `${rect.left + rect.width / 2}px`;
                          tooltip.style.top = `${rect.top - 8}px`;
                        }
                      }}
                    >
                      {Math.round(snapshotTotals.totalCost * annualisationFactor).toLocaleString()}
                      <span className="tooltip-content z-float hidden group-hover/cost:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                        <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Cost (Total)</div>
                        <div className="px-4 py-3 space-y-2 text-center">
                          <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                          <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(snapshotTotals.totalCost).toLocaleString()}</span></div>
                          <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(snapshotTotals.totalCost * annualisationFactor).toLocaleString()}</span></div>
                        </div>
                      </span>
                    </span>
                  ) : profitabilitySnapshot?.byVehicle && Object.keys(profitabilitySnapshot.byVehicle).length > 0 ? (
                    Math.round(snapshotTotals.totalCost).toLocaleString()
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-3 px-4 text-right text-success">
                  {(() => {
                    const totalTCO = Math.ceil(effectiveVehicleTypes.reduce((sum, v) => sum + (getTCOWithAnnualCost(v) * v.quantity), 0));
                    return (
                      <span
                        className="inline-block cursor-help"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTcoTooltip({
                            left: rect.left + rect.width / 2,
                            top: rect.top - 8,
                            vehicleId: null,
                            isTotal: true
                          });
                        }}
                        onMouseLeave={() => setTcoTooltip(null)}
                      >
                        {totalTCO.toLocaleString()}
                      </span>
                    );
                  })()}
                </td>
                <td className="py-3 px-4 text-right text-white annual-revenue-column-glow-static">
                  {profitabilitySnapshot?.byVehicle && Object.keys(profitabilitySnapshot.byVehicle).length > 0 && periodDays > 0 ? (
                    <span
                      className="inline-block group/revenue cursor-help"
                      onMouseMove={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-content') as HTMLElement;
                        if (tooltip && tooltip.style.display !== 'none') {
                          const rect = e.currentTarget.getBoundingClientRect();
                          tooltip.style.left = `${rect.left + rect.width / 2}px`;
                          tooltip.style.top = `${rect.top - 8}px`;
                        }
                      }}
                    >
                      {Math.round(snapshotTotals.revenue * annualisationFactor).toLocaleString()}
                      <span className="tooltip-content z-float hidden group-hover/revenue:flex flex-col fixed -translate-x-1/2 -translate-y-full min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground whitespace-pre pointer-events-none">
                        <div className="px-4 py-2 border-b border-border font-semibold text-center">Annual Revenue (Total)</div>
                        <div className="px-4 py-3 space-y-2 text-center">
                          <div><span className="text-muted-foreground">Period: </span><span className="text-white">{periodDays} days</span></div>
                          <div><span className="text-muted-foreground">Original (period): </span><span className="text-white">£{Math.round(snapshotTotals.revenue).toLocaleString()}</span></div>
                          <div><span className="text-muted-foreground">Annualised: </span><span className="text-success font-semibold">£{Math.round(snapshotTotals.revenue * annualisationFactor).toLocaleString()}</span></div>
                        </div>
                      </span>
                    </span>
                  ) : profitabilitySnapshot?.byVehicle && Object.keys(profitabilitySnapshot.byVehicle).length > 0 ? (
                    Math.round(snapshotTotals.revenue).toLocaleString()
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
          )}

          {/* Future Fleet Table */}
          {activeFleetView === 'future' && (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-foreground font-medium border-r border-border hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Type</th>
                  <th className="text-left py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Quantity Before</th>
                  <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Quantity After</th>
                  <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Unit Cost (£)</th>
                  <th className="text-right py-3 px-4 text-foreground font-medium hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">TCO (£/yr)</th>
                </tr>
              </thead>
              <tbody>
                {getFutureFleetConfiguration.map((vehicle) => {
                  const tco = calculateTCO(vehicle);
                  const originalVehicle = effectiveVehicleTypes.find(v => v.id === vehicle.id);
                  const quantityBefore = originalVehicle ? originalVehicle.quantity : 0;
                  const quantityAfter = vehicle.quantity;
                  const totalAdded = quantityAfter - quantityBefore;
                  // Use same project list as getFutureFleetConfiguration (active plan, additions only)
                  const activePlanProjects = activePlanId
                    ? replacementProjects.filter(p => p.planId === activePlanId)
                    : replacementProjects.filter(p => !p.planId);
                  let projectsApplying = activePlanProjects.filter(p => p.quantity > 0);
                  let projectsRemoving = activePlanProjects.filter(p => p.quantity < 0);
                  const sameVehicleFilter = (p: { vehicleTypeId?: string }) => {
                    if (useCase === 'logistics' && (vehicle.id === 'diesel-truck' || vehicle.id === 'electric-truck')) {
                      return p.vehicleTypeId === 'diesel-truck' || p.vehicleTypeId === 'electric-truck';
                    }
                    return p.vehicleTypeId === vehicle.id;
                  };
                  const dieselEvFilter = (forDiesel: boolean) => (p: { isDiesel: boolean; isEV: boolean }) =>
                    forDiesel ? p.isDiesel : p.isEV;
                  if (useCase === 'logistics' && (vehicle.id === 'diesel-truck' || vehicle.id === 'electric-truck')) {
                    projectsApplying = projectsApplying.filter(p => sameVehicleFilter(p) && dieselEvFilter(vehicle.id === 'diesel-truck')(p));
                    projectsRemoving = projectsRemoving.filter(p => sameVehicleFilter(p) && dieselEvFilter(vehicle.id === 'diesel-truck')(p));
                  } else {
                    projectsApplying = projectsApplying.filter(p => p.vehicleTypeId === vehicle.id);
                    projectsRemoving = projectsRemoving.filter(p => p.vehicleTypeId === vehicle.id);
                  }
                  const evAdded = projectsApplying.filter(p => p.isEV).reduce((s, p) => s + p.quantity, 0);
                  const dieselAdded = projectsApplying.filter(p => p.isDiesel).reduce((s, p) => s + p.quantity, 0);
                  const evRemoved = projectsRemoving.filter(p => p.isEV).reduce((s, p) => s + Math.abs(p.quantity), 0);
                  const dieselRemoved = projectsRemoving.filter(p => p.isDiesel).reduce((s, p) => s + Math.abs(p.quantity), 0);
                  const addedParts: string[] = [];
                  if (evAdded > 0) addedParts.push(`Electric x ${evAdded}`);
                  if (dieselAdded > 0) addedParts.push(`Diesel x ${dieselAdded}`);
                  const removedParts: string[] = [];
                  if (evRemoved > 0) removedParts.push(`Electric x ${evRemoved}`);
                  if (dieselRemoved > 0) removedParts.push(`Diesel x ${dieselRemoved}`);
                  
                  return (
                    <tr key={vehicle.id} className="border-b border-border/50 hover:bg-secondary/30 transition-all cursor-pointer group">
                      <td className="py-3 px-4 text-white font-medium border-r border-border group-hover:text-success transition-all">{redactVehicleType(vehicle.name, isDemoMode)}</td>
                      <td
                        className="py-3 px-4 text-foreground group-hover:text-success transition-all"
                        title={getFleetSizeTooltip(originalVehicle ?? vehicle) || undefined}
                      >
                        {(originalVehicle ?? vehicle).quantity}
                      </td>
                      <td className="py-3 px-4 text-right text-success font-medium group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">
                        <span>
                          {quantityAfter}
                          {addedParts.length > 0 && (
                            <span className="text-success"> (+ {addedParts.join(', ')})</span>
                          )}
                          {removedParts.length > 0 && (
                            <span className="text-destructive"> (- {removedParts.join(', ')})</span>
                          )}
                          {addedParts.length === 0 && removedParts.length === 0 && totalAdded !== 0 && (
                            totalAdded > 0 ? (
                              <span className="text-success"> (+ {totalAdded})</span>
                            ) : (
                              <span className="text-destructive"> (- {Math.abs(totalAdded)})</span>
                            )
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-foreground group-hover:text-success transition-all">{vehicle.unitCost.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-success font-semibold group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">{tco.toFixed(0).toLocaleString()}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-semibold">
                  <td className="py-3 px-4 text-white border-r border-border">TOTAL</td>
                  <td className="py-3 px-4 text-right text-white">
                    {effectiveVehicleTypes.reduce((sum, v) => sum + v.quantity, 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-success">
                    {getFutureFleetConfiguration.reduce((sum, v) => sum + v.quantity, 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-white">
                    {getFutureFleetConfiguration.reduce((sum, v) => sum + (v.quantity * v.unitCost), 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-success">
                    {Math.ceil(getFutureFleetConfiguration.reduce((sum, v) => {
                      const tco = calculateTCO(v);
                      return sum + (tco * v.quantity);
                    }, 0)).toLocaleString()}
                  </td>
                </tr>
            </tbody>
          </table>
          </div>
        )}
      </div>
      
      {showEditableTooltip && editableTooltipPosition && editableTooltipText && createPortal(
        <div
          className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${editableTooltipPosition.x}px`,
            top: `${editableTooltipPosition.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '280px'
          }}
        >
          <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">{editableTooltipText.title}</div>
          <div className="text-foreground leading-relaxed">
            {editableTooltipText.description}
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
        </div>,
        document.body
      )}

      {tcoTooltip && (tcoTooltip.isTotal || effectiveVehicleTypes.some(v => v.id === tcoTooltip.vehicleId)) && createPortal(
        <div
          className="fixed z-float flex flex-col min-w-65 max-w-80 bg-popover border border-border rounded-lg shadow-xl text-xs text-foreground pointer-events-none"
          style={{
            left: `${tcoTooltip.left}px`,
            top: `${tcoTooltip.top}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          {tcoTooltip.isTotal ? (
            <>
              <div className="px-4 py-2 border-b border-border font-semibold text-center">TCO (£/yr) total</div>
              <div className="px-4 py-3 space-y-2 text-center">
                <div><span className="text-muted-foreground">Sum of (per-vehicle TCO × quantity) for each vehicle type.</span></div>
                <div className="pt-1 border-t border-border"><span className="text-success font-semibold">£{Math.ceil(effectiveVehicleTypes.reduce((sum, v) => sum + (getTCOWithAnnualCost(v) * v.quantity), 0)).toLocaleString()}</span></div>
              </div>
            </>
          ) : (() => {
            const vehicle = effectiveVehicleTypes.find(v => v.id === tcoTooltip.vehicleId);
            if (!vehicle) return null;
            const active = activeCountByVehicleType[vehicle.id] ?? 0;
            const periodCost = profitabilityByVehicleType[vehicle.id]?.totalCost;
            const hasProfitability = active > 0 && periodCost != null && periodDays > 0 && annualisationFactor > 0;
            const capitalAnnualised = Math.round(vehicle.unitCost / vehicle.serviceLife);
            const tcoValue = getTCOWithAnnualCost(vehicle);
            return (
              <>
                <div className="px-4 py-2 border-b border-border font-semibold text-center">TCO (£/yr) calculation</div>
                <div className="px-4 py-3 space-y-2 text-center">
                  <div><span className="text-muted-foreground">Capital (annualised): </span><span className="text-white">Unit Cost ÷ Life = £{vehicle.unitCost.toLocaleString()} ÷ {vehicle.serviceLife} = £{capitalAnnualised.toLocaleString()}</span></div>
                  <div><span className="text-muted-foreground">Op. Cost (£/yr): </span><span className="text-white">£{vehicle.operationalCost.toLocaleString()}</span></div>
                  {hasProfitability ? (
                    <>
                      <div><span className="text-muted-foreground">Cost from profitability (per vehicle): </span><span className="text-white">Annual Cost ÷ active = £{Math.round(periodCost * annualisationFactor).toLocaleString()} ÷ {active} = £{Math.round((periodCost * annualisationFactor) / active).toLocaleString()}</span></div>
                      <div className="pt-1 border-t border-border"><span className="text-muted-foreground">Total (per vehicle): </span><span className="text-success font-semibold">£{Math.round(tcoValue).toLocaleString()}</span></div>
                    </>
                  ) : (
                    <>
                      <div><span className="text-muted-foreground">Fuel Cost (£/yr): </span><span className="text-white">£{(vehicle.fuelCost ?? 0).toLocaleString()}</span></div>
                      <div className="pt-1 border-t border-border"><span className="text-muted-foreground">Total (per vehicle): </span><span className="text-success font-semibold">£{Math.round(tcoValue).toLocaleString()}</span></div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>,
        document.body
      )}

      {/* Plan Management Section */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Plans</h3>
            <Button
              variant="secondary" size="sm" type="button"
              onClick={handleCreatePlan}
              className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
            >
              <Plus aria-hidden className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
              <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">New Plan</span>
            </Button>
          </div>
          <div className="flex flex-wrap gap-3">
            {plans.map(plan => {
              const planProjectCount = replacementProjects.filter(p => p.planId === plan.id).length;
              return (
                <div
                  key={plan.id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-all cursor-pointer ${
                    activePlanId === plan.id
                      ? 'bg-success/30 border-success text-success'
                      : 'bg-secondary border-border text-foreground hover:bg-accent hover:border-border'
                  }`}
                  onClick={() => handleSetActivePlan(plan.id)}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: plan.color }}
                  />
                  <span className="font-medium">{plan.name}</span>
                  <span className="text-xs opacity-70">({planProjectCount} projects)</span>
                  <Button
                    variant="ghost" size="iconSm" type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePlan(plan.id);
                    }}
                    className="ml-2 text-destructive"
                    title="Delete plan"
                  >
                    <Trash2 aria-hidden className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

      {/* Fleet Replacement Timeline */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-white">Fleet Replacement Plan</h3>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-destructive">Inflation rate:</p>
              {editingAnnualIncrease ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={annualIncreasePercent}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 0 && value <= 100) {
                        setAnnualIncreasePercent(value);
                      }
                    }}
                    onBlur={() => setEditingAnnualIncrease(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        setEditingAnnualIncrease(false);
                      }
                    }}
                    className="w-12 px-1.5 py-0.5 text-xs bg-secondary text-white rounded border border-destructive focus:outline-none focus:ring-1 focus:ring-destructive"
                    autoFocus
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <span className="text-xs text-destructive">%</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="link" size="sm" type="button"
                    onClick={() => setEditingAnnualIncrease(true)}
                    className="text-xs text-destructive"
                    style={{ cursor: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E\") 0 20, text" }}
                  >
                    {annualIncreasePercent}% per annum increase
                  </Button>
                </div>
              )}
            </div>
            {/* Current Plan Indicator */}
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground">Current Plan:</p>
                {activePlanId ? (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: plans.find(p => p.id === activePlanId)?.color || planColors[0] }}
                    />
                    <span className="text-xs text-success font-medium">
                      {plans.find(p => p.id === activePlanId)?.name || 'Plan 1'}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">No plan selected</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <Button
            variant="secondary" size="sm" type="button"
            onClick={() => setShowAddProject(!showAddProject)}
            className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
          >
            <Plus aria-hidden className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
              <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Add Schedule</span>
          </Button>
          </div>
        </div>

        {showAddProject && (
          <div className="mb-4 p-4 bg-secondary/50 rounded-lg border border-border">
            <div className="flex flex-wrap items-center gap-4">
              <select
                value={newProject.vehicleTypeId || ''}
                onChange={(e) => setNewProject({ ...newProject, vehicleTypeId: e.target.value })}
                className="min-w-50 px-3 py-2 bg-accent text-white rounded-md border border-border hover:border-success hover:bg-accent focus:outline-none focus:ring-2 focus:ring-success transition-all cursor-pointer"
              >
                <option value="">Select Vehicle Type</option>
                {effectiveVehicleTypes.map(v => (
                  <option key={v.id} value={v.id}>{redactVehicleType(v.name, isDemoMode)}</option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Year"
                value={newProject.year || ''}
                onChange={(e) => setNewProject({ ...newProject, year: parseInt(e.target.value) || START_YEAR })}
                min={START_YEAR}
                max={END_YEAR}
                className="px-3 py-2 bg-accent text-white rounded-md border border-border hover:border-success hover:bg-accent focus:outline-none focus:ring-2 focus:ring-success transition-all"
              />
              <input
                type="number"
                placeholder="Quantity"
                value={newProject.quantity || ''}
                onChange={(e) => setNewProject({ ...newProject, quantity: parseInt(e.target.value) || 1 })}
                min={1}
                className="px-3 py-2 bg-accent text-white rounded-md border border-border hover:border-success hover:bg-accent focus:outline-none focus:ring-2 focus:ring-success transition-all"
              />
              <div className="flex flex-col gap-2">
                {useCase === 'logistics' ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default" size="icon" type="button"
                      onClick={() => {
                        const isEV = newProject.vehicleTypeId === 'electric-truck';
                        const isDiesel = !isEV;
                        handleAddProject(isDiesel, isEV, false);
                      }}
                      className="h-7 w-7 group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                      title="Add vehicles"
                    >
                      <Plus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-success-md transition-all" />
                    </Button>
                    <Button
                      variant="destructive" size="icon" type="button"
                      onClick={() => {
                        const isEV = newProject.vehicleTypeId === 'electric-truck';
                        const isDiesel = !isEV;
                        handleAddProject(isDiesel, isEV, true);
                      }}
                      className="h-7 w-7 group border border-transparent hover:border-destructive hover:shadow-lg hover:shadow-destructive/50"
                      title="Remove vehicles"
                    >
                      <Minus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-critical-md transition-all" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground">Diesel:</span>
                      <Button
                        variant="default" size="icon" type="button"
                        onClick={() => handleAddProject(true, false, false)}
                        className="h-7 w-7 group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                        title="Add Diesel vehicles"
                      >
                        <Plus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-success-md transition-all" />
                      </Button>
                      <Button
                        variant="destructive" size="icon" type="button"
                        onClick={() => handleAddProject(true, false, true)}
                        className="h-7 w-7 group border border-transparent hover:border-destructive hover:shadow-lg hover:shadow-destructive/50"
                        title="Remove Diesel vehicles"
                      >
                        <Minus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-critical-md transition-all" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground">EV:</span>
                      {(() => {
                        const selectedVehicle = effectiveVehicleTypes.find(v => v.id === newProject.vehicleTypeId);
                        let hasEVInFleet = false;
                        
                        if (selectedVehicle) {
                          // For Emergency Services: check if the selected vehicle type has EV capability and EVs exist
                          // In ES mode, we assume if evCost is defined, the vehicle type can have EVs
                          // Check if there are any EV projects for this vehicle type or if the vehicle type supports EVs
                          hasEVInFleet = selectedVehicle.evCost !== undefined;
                          // Also check if there are existing EV projects for this vehicle type
                          const hasEVProjects = replacementProjects.some(p => 
                            p.vehicleTypeId === selectedVehicle.id && p.isEV && p.quantity > 0
                          );
                          hasEVInFleet = hasEVInFleet || hasEVProjects;
                        }
                        
                        return (
                          <>
                            {hasEVInFleet && (
                              <Button
                                variant="default" size="icon" type="button"
                                onClick={() => handleAddProject(false, true, false)}
                                className="h-7 w-7 group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                                title="Add EV vehicles"
                              >
                                <Plus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-success-md transition-all" />
                              </Button>
                            )}
                            {hasEVInFleet && (
                              <Button
                                variant="destructive" size="icon" type="button"
                                onClick={() => handleAddProject(false, true, true)}
                                className="h-7 w-7 group border border-transparent hover:border-destructive hover:shadow-lg hover:shadow-destructive/50"
                                title="Remove EV vehicles"
                              >
                                <Minus aria-hidden className="w-4 h-4 group-hover:drop-shadow-glow-critical-md transition-all" />
                              </Button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="secondary" size="md" type="button"
                  onClick={() => setShowAddProject(false)}
                  className="bg-accent hover:bg-secondary text-white"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

      {getActivePlanProjects.length === 0 && (
        <div className="mb-3 px-4 py-3 rounded-lg border border-dashed border-border bg-card/40 text-center">
          <p className="text-sm text-muted-foreground">
            No replacement projects in {plans.find(p => p.id === activePlanId)?.name ?? 'this plan'} yet —
            click a year cell below or <span className="text-success">Add Schedule</span> to plan one.
          </p>
        </div>
      )}

      <div className="mb-4">
        <BarChart
          title="Total annual cost (£k per year)"
          data={years.map((year) => ({
            label: String(year),
            values: [{ vehicle: 'Total annual cost', value: Math.round(calculateAnnualCost(year) / 1000), color: STATUS_COLORS.success }],
          }))}
          hideLegend
          height="h-72"
        />
      </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Year headers */}
            <div className="flex border-b-2 border-border mb-2">
              <div className="sticky left-0 z-10 w-48 flex-shrink-0 py-2 px-4 font-semibold text-foreground bg-card border-r border-border hover:text-success hover:drop-shadow-glow-success-md transition-all cursor-pointer">Type</div>
              {years.map(year => (
                <div
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`w-34 flex-shrink-0 text-center py-2 px-2 font-semibold text-foreground border-l border-border transition-all cursor-pointer hover:text-success hover:drop-shadow-glow-success-md ${
                    year === selectedYear ? 'bg-success/30 text-success drop-shadow-glow-success-md' : ''
                  }`}
                >
                  {year}
                </div>
              ))}
            </div>

            {/* Vehicle rows */}
            {effectiveVehicleTypes.map((vehicle) => {
              const schedule = getReplacementSchedule(vehicle.id);
              return (
                <div
                  key={vehicle.id}
                  className="flex border-b border-border/50 hover:bg-secondary/20 transition-all group cursor-pointer"
                  onClick={() => setNewProject(prev => ({ ...prev, vehicleTypeId: vehicle.id }))}
                >
                  <div className="sticky left-0 z-10 w-48 flex-shrink-0 py-3 px-4 text-white font-medium bg-card border-r border-border/50 group-hover:text-success transition-all">{redactVehicleType(vehicle.name, isDemoMode)}</div>
                  {years.map(year => {
                    const scheduleItem = schedule.find(s => s.year === year);
                    const isProject = scheduleItem?.type === 'project';
                    const projects = getActivePlanProjects.filter(p => p.vehicleTypeId === vehicle.id && p.year === year);
                    const dieselProjects = projects.filter(p => p.isDiesel);
                    const evProjects = projects.filter(p => p.isEV);
                    
                    const dieselAdd = dieselProjects.filter(p => p.quantity > 0);
                    const dieselRemove = dieselProjects.filter(p => p.quantity < 0);
                    const evAdd = evProjects.filter(p => p.quantity > 0);
                    const evRemove = evProjects.filter(p => p.quantity < 0);
                    
                    return (
                      <div
                        key={year}
                        className={`w-34 flex-shrink-0 border-l border-border/50 py-2 px-2 ${
                          isProject ? 'bg-warning/40' : 'bg-card'
                        } ${
                          dragOverYear === year && dragOverVehicleTypeId === vehicle.id && draggedProjectId
                            ? 'bg-success/40 border-success border-2 border-dashed' 
                            : ''
                        }`}
                        onClick={() => {
                          setSelectedYear(year);
                          setShowAddProject(true);
                          setNewProject(prev => ({ ...prev, year }));
                        }}
                        onDragOver={(e) => handleDragOver(e, year, vehicle.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, year, vehicle.id)}
                      >
                        {isProject && (dieselProjects.length > 0 || evProjects.length > 0) && (
                          <div className="text-center">
                            {dieselAdd.map(project => (
                              editingProject === project.id && editProjectData ? (
                                <div key={project.id} className="flex flex-col gap-1.5 items-center mb-0.5 p-1.5 bg-secondary/70 rounded border border-success">
                                  <div className="flex items-center gap-1.5">
                                    <select
                                      value={editProjectData.isDiesel ? 'diesel' : editProjectData.isEV ? 'ev' : ''}
                                      onChange={(e) => {
                                        const isDiesel = e.target.value === 'diesel';
                                        const isEV = e.target.value === 'ev';
                                        setEditProjectData({ ...editProjectData, isDiesel, isEV });
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="diesel">Diesel</option>
                                      <option value="ev">EV</option>
                                    </select>
                                    <Button
                                      variant={editProjectData.isRemoval ? 'destructive' : 'default'} size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditProjectData({ ...editProjectData, isRemoval: !editProjectData.isRemoval });
                                      }}
                                      className="h-auto px-1.5 py-0.5"
                                      title={editProjectData.isRemoval ? "Click to change to Add" : "Click to change to Remove"}
                                    >
                                      {editProjectData.isRemoval ? <Minus aria-hidden className="w-3 h-3" /> : <Plus aria-hidden className="w-3 h-3" />}
                                    </Button>
                                    <input
                                      type="number"
                                      value={editProjectData.quantity}
                                      onChange={(e) => {
                                        const qty = parseInt(e.target.value) || 1;
                                        setEditProjectData({ ...editProjectData, quantity: Math.max(1, qty) });
                                      }}
                                      className="w-12 text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                      min={1}
                                    />
                            </div>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      variant="default" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveProjectEdit(project.id);
                                      }}
                                      className="h-auto px-2 py-0.5"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelProjectEdit();
                                      }}
                                      className="h-auto px-2 py-0.5 bg-accent hover:bg-secondary text-white"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Cancel
                                    </Button>
                            </div>
                                </div>
                              ) : (
                                <div 
                                  key={project.id} 
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, project.id)}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProject(project);
                                  }}
                                  className={`flex items-center justify-center gap-1.5 text-xs text-muted-foreground mb-0.5 cursor-move hover:bg-secondary/50 rounded px-1 py-0.5 transition-all ${
                                    draggedProjectId === project.id ? 'opacity-50' : ''
                                  }`}
                                  title="Click to edit or drag to move to a different year"
                                  style={{ cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, move` }}
                                >
                                  <span>Diesel: +{project.quantity}</span>
                              <Button
                                variant="ghost" size="iconSm" type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                      handleDeleteProject(project.id);
                                }}
                                className="text-destructive hover:drop-shadow-glow-critical-md"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                    <Trash2 aria-hidden className="w-3 h-3" />
                              </Button>
                                </div>
                              )
                            ))}
                            {dieselRemove.map(project => (
                              editingProject === project.id && editProjectData ? (
                                <div key={project.id} className="flex flex-col gap-1.5 items-center mb-0.5 p-1.5 bg-secondary/70 rounded border border-success">
                                  <div className="flex items-center gap-1.5">
                                    <select
                                      value={editProjectData.isDiesel ? 'diesel' : editProjectData.isEV ? 'ev' : ''}
                                      onChange={(e) => {
                                        const isDiesel = e.target.value === 'diesel';
                                        const isEV = e.target.value === 'ev';
                                        setEditProjectData({ ...editProjectData, isDiesel, isEV });
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="diesel">Diesel</option>
                                      <option value="ev">EV</option>
                                    </select>
                                    <Button
                                      variant={editProjectData.isRemoval ? 'destructive' : 'default'} size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditProjectData({ ...editProjectData, isRemoval: !editProjectData.isRemoval });
                                      }}
                                      className="h-auto px-1.5 py-0.5"
                                      title={editProjectData.isRemoval ? "Click to change to Add" : "Click to change to Remove"}
                                    >
                                      {editProjectData.isRemoval ? <Minus aria-hidden className="w-3 h-3" /> : <Plus aria-hidden className="w-3 h-3" />}
                                    </Button>
                                    <input
                                      type="number"
                                      value={editProjectData.quantity}
                                      onChange={(e) => {
                                        const qty = parseInt(e.target.value) || 1;
                                        setEditProjectData({ ...editProjectData, quantity: Math.max(1, qty) });
                                      }}
                                      className="w-12 text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                      min={1}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      variant="default" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveProjectEdit(project.id);
                                      }}
                                      className="h-auto px-2 py-0.5"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelProjectEdit();
                                      }}
                                      className="h-auto px-2 py-0.5 bg-accent hover:bg-secondary text-white"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  key={project.id} 
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, project.id)}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProject(project);
                                  }}
                                  className={`flex items-center justify-center gap-1.5 text-xs text-destructive mb-0.5 cursor-move hover:bg-secondary/50 rounded px-1 py-0.5 transition-all ${
                                    draggedProjectId === project.id ? 'opacity-50' : ''
                                  }`}
                                  title="Click to edit or drag to move to a different year"
                                  style={{ cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, move` }}
                                >
                                  <span>Diesel: {project.quantity}</span>
                                  <Button
                                    variant="ghost" size="iconSm" type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteProject(project.id);
                                    }}
                                    className="text-destructive hover:drop-shadow-glow-critical-md"
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 aria-hidden className="w-3 h-3" />
                                  </Button>
                                </div>
                              )
                            ))}
                            {evAdd.map(project => (
                              editingProject === project.id && editProjectData ? (
                                <div key={project.id} className="flex flex-col gap-1.5 items-center mb-0.5 p-1.5 bg-secondary/70 rounded border border-success">
                                  <div className="flex items-center gap-1.5">
                                    <select
                                      value={editProjectData.isDiesel ? 'diesel' : editProjectData.isEV ? 'ev' : ''}
                                      onChange={(e) => {
                                        const isDiesel = e.target.value === 'diesel';
                                        const isEV = e.target.value === 'ev';
                                        setEditProjectData({ ...editProjectData, isDiesel, isEV });
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="diesel">Diesel</option>
                                      <option value="ev">EV</option>
                                    </select>
                                    <Button
                                      variant={editProjectData.isRemoval ? 'destructive' : 'default'} size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditProjectData({ ...editProjectData, isRemoval: !editProjectData.isRemoval });
                                      }}
                                      className="h-auto px-1.5 py-0.5"
                                      title={editProjectData.isRemoval ? "Click to change to Add" : "Click to change to Remove"}
                                    >
                                      {editProjectData.isRemoval ? <Minus aria-hidden className="w-3 h-3" /> : <Plus aria-hidden className="w-3 h-3" />}
                                    </Button>
                                    <input
                                      type="number"
                                      value={editProjectData.quantity}
                                      onChange={(e) => {
                                        const qty = parseInt(e.target.value) || 1;
                                        setEditProjectData({ ...editProjectData, quantity: Math.max(1, qty) });
                                      }}
                                      className="w-12 text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                      min={1}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      variant="default" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveProjectEdit(project.id);
                                      }}
                                      className="h-auto px-2 py-0.5"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelProjectEdit();
                                      }}
                                      className="h-auto px-2 py-0.5 bg-accent hover:bg-secondary text-white"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  key={project.id} 
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, project.id)}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProject(project);
                                  }}
                                  className={`flex items-center justify-center gap-1.5 text-xs text-muted-foreground mb-0.5 cursor-move hover:bg-secondary/50 rounded px-1 py-0.5 transition-all ${
                                    draggedProjectId === project.id ? 'opacity-50' : ''
                                  }`}
                                  title="Click to edit or drag to move to a different year"
                                  style={{ cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, move` }}
                                >
                                  <span>EV: +{project.quantity}</span>
                                  <Button
                                    variant="ghost" size="iconSm" type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteProject(project.id);
                                    }}
                                    className="text-destructive hover:drop-shadow-glow-critical-md"
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 aria-hidden className="w-3 h-3" />
                                  </Button>
                                </div>
                              )
                            ))}
                            {evRemove.map(project => (
                              editingProject === project.id && editProjectData ? (
                                <div key={project.id} className="flex flex-col gap-1.5 items-center mb-0.5 p-1.5 bg-secondary/70 rounded border border-success">
                                  <div className="flex items-center gap-1.5">
                                    <select
                                      value={editProjectData.isDiesel ? 'diesel' : editProjectData.isEV ? 'ev' : ''}
                                      onChange={(e) => {
                                        const isDiesel = e.target.value === 'diesel';
                                        const isEV = e.target.value === 'ev';
                                        setEditProjectData({ ...editProjectData, isDiesel, isEV });
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="diesel">Diesel</option>
                                      <option value="ev">EV</option>
                                    </select>
                                    <Button
                                      variant={editProjectData.isRemoval ? 'destructive' : 'default'} size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditProjectData({ ...editProjectData, isRemoval: !editProjectData.isRemoval });
                                      }}
                                      className="h-auto px-1.5 py-0.5"
                                      title={editProjectData.isRemoval ? "Click to change to Add" : "Click to change to Remove"}
                                    >
                                      {editProjectData.isRemoval ? <Minus aria-hidden className="w-3 h-3" /> : <Plus aria-hidden className="w-3 h-3" />}
                                    </Button>
                                    <input
                                      type="number"
                                      value={editProjectData.quantity}
                                      onChange={(e) => {
                                        const qty = parseInt(e.target.value) || 1;
                                        setEditProjectData({ ...editProjectData, quantity: Math.max(1, qty) });
                                      }}
                                      className="w-12 text-xs px-1.5 py-0.5 bg-accent text-white rounded border border-border focus:outline-none focus:ring-1 focus:ring-success"
                                      onClick={(e) => e.stopPropagation()}
                                      min={1}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      variant="default" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveProjectEdit(project.id);
                                      }}
                                      className="h-auto px-2 py-0.5"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary" size="sm" type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelProjectEdit();
                                      }}
                                      className="h-auto px-2 py-0.5 bg-accent hover:bg-secondary text-white"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  key={project.id} 
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, project.id)}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProject(project);
                                  }}
                                  className={`flex items-center justify-center gap-1.5 text-xs text-destructive mb-0.5 cursor-move hover:bg-secondary/50 rounded px-1 py-0.5 transition-all ${
                                    draggedProjectId === project.id ? 'opacity-50' : ''
                                  }`}
                                  title="Click to edit or drag to move to a different year"
                                  style={{ cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 0 20, move` }}
                                >
                                  <span>EV: {project.quantity}</span>
                                  <Button
                                    variant="ghost" size="iconSm" type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteProject(project.id);
                                    }}
                                    className="text-destructive hover:drop-shadow-glow-critical-md"
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 aria-hidden className="w-3 h-3" />
                                  </Button>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Total Annual Cost row */}
            <div className="sticky bottom-0 z-20 flex border-t-2 border-border font-semibold bg-secondary/95 backdrop-blur-sm">
              <div className="sticky left-0 z-30 w-48 flex-shrink-0 py-3 px-4 text-white bg-secondary/95 backdrop-blur-sm border-r border-border">TOTAL ANNUAL COST</div>
              {years.map(year => {
                const annualCost = calculateAnnualCost(year);
                return (
                  <div key={year} className="w-34 flex-shrink-0 border-l border-border text-center py-2 px-2 text-success bg-secondary/95 backdrop-blur-sm">
                    £{(annualCost / 1000).toFixed(0)}k
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Cost Comparison Chart */}
      {plans.length > 1 && getPlanComparisonData.length > 0 && (
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
          <h3 className="text-lg font-semibold text-white mb-4">Plan Cost Comparison</h3>
          <LineChart
            series={getPlanComparisonData}
            yAxisLabel="Annual Cost (£)"
            heightClass="min-h-100"
            chartHeight={350}
            formatYAxisAsK={true}
          />
        </div>
      )}

      {/* Cost Comparison: Diesel vs Electric */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Fuel className="w-5 h-5 text-warning" />
            Diesel Fleet TCO (Annual)
          </h3>
          <div className="space-y-3">
            {effectiveVehicleTypes
              .filter(vehicle => (useCase === 'logistics' ? vehicle.id === 'diesel-truck' : true))
              .map(vehicle => {
                const tco = calculateTCO(vehicle, false);
                const totalAnnual = tco * vehicle.quantity;
                return (
                  <div key={vehicle.id} className="p-3 bg-secondary/30 rounded hover:bg-secondary/50 transition-all cursor-pointer group">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-foreground group-hover:text-success transition-all">{vehicle.name}</span>
                      <span className="text-white font-semibold group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">£{tco.toFixed(0).toLocaleString()}/vehicle/yr</span>
                    </div>
                    <div className="text-xs text-muted-foreground group-hover:text-foreground transition-all">
                      Total: £{totalAnnual.toFixed(0).toLocaleString()}/yr ({vehicle.quantity} vehicles)
                    </div>
                  </div>
                );
              })}
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="text-white font-semibold">Total Fleet TCO</span>
                <span className="text-success font-bold">
                  £{effectiveVehicleTypes
                    .filter(v => (useCase === 'logistics' ? v.id === 'diesel-truck' : true))
                    .reduce((sum, v) => sum + (calculateTCO(v, false) * v.quantity), 0)
                    .toFixed(0)
                    .toLocaleString()}/yr
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Battery className="w-5 h-5 text-success" />
            Electric Fleet TCO (Annual)
          </h3>
          <div className="space-y-3">
            {effectiveVehicleTypes
              .filter(vehicle => vehicle.id === 'electric-truck')
              .map(vehicle => {
                const tco = vehicle.evCost ? calculateTCO(vehicle, true) : null;
                const totalAnnual = tco ? tco * vehicle.quantity : 0;
                return (
                  <div key={vehicle.id} className="p-3 bg-secondary/30 rounded hover:bg-secondary/50 transition-all cursor-pointer group">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-foreground group-hover:text-success transition-all">{vehicle.name}</span>
                      <span className="text-white font-semibold group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">
                        {tco ? `£${tco.toFixed(0).toLocaleString()}/vehicle/yr` : 'N/A'}
                      </span>
                    </div>
                    {tco && (
                      <div className="text-xs text-muted-foreground group-hover:text-foreground transition-all">
                        Total: £{totalAnnual.toFixed(0).toLocaleString()}/yr ({vehicle.quantity} vehicles)
                      </div>
                    )}
                  </div>
                );
              })}
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="text-white font-semibold">Total Fleet TCO</span>
                <span className="text-success font-bold">
                  £{effectiveVehicleTypes
                    .filter(v => v.id === 'electric-truck')
                    .reduce((sum, v) => {
                      const tco = v.evCost ? calculateTCO(v, true) : 0;
                      return sum + (tco * v.quantity);
                    }, 0)
                    .toFixed(0)
                    .toLocaleString()}/yr
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Infrastructure Planning */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-ring" />
            Infrastructure Schedule
          </h3>
          <Button variant="secondary" size="sm" type="button" className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50">
            <Plus aria-hidden className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
            <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Add Infrastructure Project</span>
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Plan charging infrastructure, depots, and stations required for fleet electrification.
        </p>
        <div className="text-center py-8 text-muted-foreground">
          Infrastructure planning coming soon. Link to optimisation results and vehicle replacement projects.
        </div>
      </div>

      {/* Unmapped Vehicles Modal */}
      {showUnmappedVehiclesModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => setShowUnmappedVehiclesModal(false)}
        >
          <div
            className="bg-card rounded-lg shadow-xl border border-border max-w-2xl w-full max-h-screen-80 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-white">
                Unmapped Vehicles ({unmapped.count})
              </h3>
              <Button
                variant="ghost" size="icon" type="button"
                onClick={() => setShowUnmappedVehiclesModal(false)}
              >
                <X aria-hidden className="w-5 h-5 text-muted-foreground hover:text-white" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-muted-foreground mb-4">
                These vehicles have profitability data but are not in your vehicle list (Supatrak or Optifleet). Add them to the list to assign a vehicle type.
              </p>
              <div className="space-y-2">
                {unmapped.vehicleIds.length > 0 ? (
                  unmapped.vehicleIds.map((vehicleId) => (
                    <div
                      key={vehicleId}
                      className="px-4 py-2 bg-secondary/50 rounded border border-border hover:border-success hover:bg-secondary transition-all"
                    >
                      <span className="text-foreground font-mono">{redactVehicleId(vehicleId, isDemoMode)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground italic">No unmapped vehicles</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}